import moment from '@tw/moment-cached/module/timezone';
import momentNoTimezone from '@tw/moment-cached';
import {
  chartOrders,
  chartOrdersWithAmount,
  chartSales,
  totalOrders,
  totalOrdersWithAmount,
  totalSales,
} from './generalSelectors';
import {
  convertWillyToMetricsBreakdown,
  createPartialState,
  distributeInteger,
  emptyArray,
  expressionChart,
  getActiveShippingProvider,
  getCustomExpenseAttributionType,
  getCustomExpenseSmartAttribution,
  getCustomMetrics,
  getCustomSpendCategories,
  getCustomSpends,
  getExternalApps,
  getMainDatePickerSelectionRange,
  getNewStats,
  getNewStatsService,
  getPaymentGatewayCosts,
  getPreCalculatedUniqueCustomerCount,
  getShippingCosts,
  getStats,
  isServer,
  partialStateDependencies,
  safeDivide,
  salesBySegmentChart,
  selectedStatsServices,
  simpleChart,
  simpleChart2,
  simpleChart3,
  simpleChart4,
  simpleChartMinus,
  simpleChartPlus,
  simpleChartWithAlternative,
  timezone,
  total,
  totalInventoryCost,
  totalInventoryItems,
  totalInventoryItemsMissing,
  totalInventoryValue,
  totalWithAlternative,
  useShippingChargesAsCost,
  willySummaryBox,
} from './generalUtils';
import tModules from './modules';
import services from './services';
import tServices from './services';
import amazon from './services/amazon';
import { SummaryMetrics } from '@tw/types/module/SummaryMetrics';
import { metrics } from '@tw/constants/module/Metrics/allMetrics';
import { Granularity, valueFormats } from '@tw/types';
import { createSelector, lodashMemoize } from './reselect';
import { isEqual } from 'lodash';
import { convertDataToStats } from './convertDataToStats';
import { ServicesIds } from '@tw/types/module/services';

const getShopifyStats = getNewStatsService('shopify', true);
const getShopifySegmentStats = getNewStatsService('shopify-segment');

const influencerSpend = tServices.influencerSpend;
const influencerSpendChart = tServices.influencerSpendChart;

const shippingCostPropPerProvider: Partial<{ [key in ServicesIds]: string }> = {
  shopify: 'total_shipping_costs',
  shipstation: 'shipments_cost',
  shipbob: 'fulfillment_cost',
};

const {
  pinterestSpend,
  pinterestSpendChart,
  chartKlaviyoPlacedOrderSalesPercentFlows,
  chartKlaviyoPlacedOrderSalesPercentCampaigns,
  chartKlaviyoPlacedOrderSalesPercent,
  tiktokSpend,
  tiktokSpendChart,
  twitterSpend,
  twitterSpendChart,
  tiktokConversionValue,
  tiktokConversionValueChart,
  twitterConversionValue,
  twitterConversionValueChart,
  totalGoogleAdsCost,
  chartGoogleAds,
  googleConversionValue,
  googleConversionValueChart,
  chartGoogleRoas,
  chartSnapchatAds,
  totalSnapchatSpend,
  totalSnapchatConversionPurchasesValue,
  chartSnapchatConversionPurchasesValue,
  totalFacebookInsights,
  chartFacebookInsights,
  facebookPurchaseConversionValue,
  facebookConversionValueChart,
  chartFacebookCpc,
  chartFacebookCpm,
  chartFacebookCtr,
  chartFacebookPurchaseRoas,
  chartFacebookCostPerOutboundClick,
  bingAdSpend,
  bingAdSpendChart,
  bingConversionValue,
  bingConversionValueChart,
  mountainSpend,
  mountainSpendChart,
  mountainConversionValue,
  mountainConversionValueChart,
  criteoSpend,
  criteoSpendChart,
  criteoConversionValue,
  criteoConversionValueChart,
  shipstationCost,
  shipstationCostChart,
  shipstationShipments,
  shipstationShipmentsChart,
  shipstationOrders,
  shipstationOrdersChart,
  shipstationOrdersShipped,
  shipstationOrdersShippedChart,
  shipstationOrdersUnshipped,
  shipstationOrdersUnshippedChart,
  shipstationAvgCostPerShipment,
  shipstationAvgCostPerShipmentChart,
  smsbumpRevenue,
  smsbumpRevenueChart,
  smsbumpCampaignRevenue,
  smsbumpCampaignRevenueChart,
  smsbumpFlowRevenue,
  smsbumpFlowRevenueChart,
  smsbumpSpend,
  smsbumpSpendChart,
  smsbumpSent,
  smsbumpSentChart,
  smsbumpUnsubscribed,
  smsbumpUnsubscribedChart,
  smsbumpConversions,
  smsbumpConversionsChart,
  smsbumpAov,
  smsbumpAovChart,
  smsbumpCtr,
  smsbumpCtrChart,
  smsbumpRoas,
  smsbumpRoasChart,
  smsbumpRoi,
  smsbumpRoiChart,
  smsbumpDelivered,
  smsbumpDeliveredChart,
  smsbumpClicks,
  smsbumpClicksChart,
  shipbobOrders,
  shipbobOrdersChart,
  shipbobOrdersFulfilled,
  shipbobOrdersFulfilledChart,
  shipbobPercentOfOrdersFulfilledOnTime,
  shipbobPercentOfOrdersFulfilledOnTimeChart,
  shipbobFulfillmentCost,
  shipbobFulfillmentCostChart,
  shipbobAvgFulfillmentCost,
  shipbobAvgFulfillmentCostChart,
  postscriptRevenue,
  postscriptRevenueChart,
  postscriptSent,
  postscriptSentChart,
  postscriptSubscribed,
  postscriptSubscribedChart,
  postscriptUnsubscribed,
  postscriptUnsubscribedChart,
  postscriptConversions,
  postscriptConversionsChart,
  postscriptAov,
  postscriptAovChart,
  postscriptCtr,
  postscriptCtrChart,
  postscriptDelivered,
  postscriptDeliveredChart,
  postscriptClicks,
  postscriptClicksChart,
} = services;

const {
  totalAmazonProductItemPrice,
  amazonNetProfit,
  amazonAds,
  totalAmazonOrders,
  amazonAdsConversionValue,
  chartAmazonProductItemPrice,
  chartAmazonAds,
  chartAmazonOrders,
  amazonConversionValueChart,
  cahrtAmazonNetProfit,
  amazonAOV,
  amazonAOVChart,
  amazonNetSales,
  chartAmazonNetSales,
} = amazon.SELECTORS;

const getEntireAttributionState = (state) => state;

const calculateTotalCustomSpend = (
  customSpends,
  mainDatePickerSelectionRange,
  shopTimezone,
  newStats,
  customExpenseAttributionType,
  customExpenseSmartAttribution = {}
) => {
  if (!customSpends.length) return 0;
  var spendTotal = 0;
  customSpends.forEach((customSpend) => {
    var {
      metricId,
      recurring,
      start_date,
      cost,
      percent,
      end_date,
      recurringIntervalLength,
      recurringOption,
      recurringCadence,
      id,
    } = customSpend;
    var isFixed = !metricId;

    if (
      !start_date ||
      (recurring && !recurringIntervalLength) ||
      (isFixed && !recurring && !end_date)
    )
      return;

    const spendStartDate = start_date ? moment(start_date).tz(shopTimezone) : null;
    const spendEndDate = end_date ? moment(end_date).tz(shopTimezone) : null;

    var { start, end } = mainDatePickerSelectionRange;

    cost = !cost ? 0 : Number(cost.replace(/,/g, '')); //.replace(/\./g,'')

    var firstSpendDay = spendStartDate
      ? moment.max([spendStartDate, start])
      : momentNoTimezone(start);
    var lastSpendDay = spendEndDate ? moment.min([spendEndDate, end]) : momentNoTimezone(end);

    //first make sure spend date range affects selected date range
    if (spendStartDate?.isAfter(end, 'day') || spendEndDate?.isBefore(start, 'day')) return;
    else {
      if (isFixed) {
        let dailyRecurring = recurring && (!recurringCadence || recurringCadence === 'day');
        let monthlyRecurring = recurring && recurringCadence == 'month';

        let avgDailyCost = monthlyRecurring
          ? null
          : dailyRecurring
          ? Number(cost) / Number(recurringIntervalLength)
          : Number(cost) / (spendEndDate.diff(spendStartDate, 'days') + 1);

        //last day calculations (hourly vs daily vs 'smart' attribution)
        let lastDayReduction;
        let daysInLastMonth = lastSpendDay.daysInMonth();
        const now = momentNoTimezone();
        const hour = now.hour() + 1;
        if (customExpenseAttributionType === 'hourly' && now.isSame(lastSpendDay, 'day')) {
          lastDayReduction = !monthlyRecurring
            ? (avgDailyCost * (24 - hour)) / 24
            : monthlyRecurring && Number(recurringIntervalLength) === 1
            ? ((Number(cost) / daysInLastMonth) * (24 - hour)) / 24
            : monthlyRecurring && Number(recurringIntervalLength) > 1
            ? ((Number(cost) / (recurringIntervalLength * 30.5)) * (24 - hour)) / 24
            : 0;
        } else if (
          customExpenseAttributionType === 'smart' &&
          now.isSame(lastSpendDay, 'day') &&
          customExpenseSmartAttribution?.[hour] !== undefined
        ) {
          lastDayReduction = !monthlyRecurring
            ? avgDailyCost * (1 - customExpenseSmartAttribution?.[hour])
            : monthlyRecurring && Number(recurringIntervalLength) === 1
            ? (Number(cost) / daysInLastMonth) * (1 - customExpenseSmartAttribution?.[hour])
            : monthlyRecurring && Number(recurringIntervalLength) > 1
            ? (Number(cost) / (recurringIntervalLength * 30.5)) *
              (1 - customExpenseSmartAttribution?.[hour])
            : 0;
        } else lastDayReduction = 0;

        if (dailyRecurring && spendEndDate) {
          //last cycle details
          const lastCycleLength =
            (spendEndDate.diff(spendStartDate, 'days') + 1) % Number(recurringIntervalLength);
          const lastCycleStart = spendEndDate
            .clone()
            .subtract(lastCycleLength - 1, 'days')
            .startOf('day');
          const lastCycleInDateRange = lastCycleStart.isSameOrBefore(end, 'day');
          //last cycle doesnt line up evenly and is within daterange
          if (recurringOption && lastCycleLength > 0 && lastCycleInDateRange) {
            const lastCycleSpendStart = moment.max([lastCycleStart, start]);
            //complete cycles
            spendTotal += avgDailyCost * (lastCycleSpendStart.diff(firstSpendDay, 'days') + 1);
            //last incomplete cycle
            if (recurringOption === 'calculate') {
              spendTotal += avgDailyCost * (lastSpendDay.diff(lastCycleSpendStart, 'days') + 1);
            } else if (recurringOption === 'delete') return;
          }
          //last cycle lines up evenly, or uneven cycle isnt in date range
          else spendTotal += avgDailyCost * (lastSpendDay.diff(firstSpendDay, 'days') + 1);
        }

        //monthly Recurring expense
        else if (monthlyRecurring && Number(recurringIntervalLength) === 1) {
          let lastCycleStart;
          let cycleStartDate = spendStartDate.date();
          if (spendEndDate) {
            //check if last cycle is complete
            if (spendEndDate.date() < cycleStartDate) {
              let prevMonth = spendEndDate.subtract(1, 'month');
              lastCycleStart = prevMonth.date(cycleStartDate);
            } else lastCycleStart = spendEndDate.clone().date(cycleStartDate);
          }

          //calculations for monthly recurring and last incomplete cycle is in date range
          if (spendEndDate && recurringOption && lastCycleStart?.isSameOrBefore(end, 'day')) {
            let currentDay = firstSpendDay.clone();
            while (currentDay < lastCycleStart) {
              let daysInCurrentCycle = 0;
              if (currentDay.date() >= cycleStartDate)
                daysInCurrentCycle = currentDay.daysInMonth();
              else daysInCurrentCycle = currentDay.clone().subtract(1, 'month').daysInMonth();
              //full cycle is included in date range
              if (lastCycleStart.diff(currentDay, 'days') + 1 > daysInCurrentCycle) {
                spendTotal += Number(cost);
                currentDay.add(daysInCurrentCycle, 'days');
              }
              //partial cycle is in date range
              else {
                spendTotal +=
                  (Number(cost) / daysInCurrentCycle) *
                  (lastCycleStart.diff(currentDay, 'days') + 1);
                currentDay.add(lastCycleStart.diff(currentDay, 'days') + 1, 'day');
              }
            }
            if (recurringOption === 'calculate') {
              while (currentDay <= lastSpendDay) {
                let daysInCurrentCycle = 0;
                if (currentDay.date() >= cycleStartDate)
                  daysInCurrentCycle = currentDay.daysInMonth();
                else daysInCurrentCycle = currentDay.clone().subtract(1, 'month').daysInMonth();
                spendTotal += Number(cost) / daysInCurrentCycle;
                currentDay.add(1, 'days');
              }
            } else if (recurringOption === 'delete') return;
          }
          //monthly recurring with no end date / end date with complete cycle / or end date with incomplete cycle not in date range
          else {
            let cycleStartDate = spendStartDate.date();
            let currentDay = firstSpendDay.clone();
            while (currentDay <= lastSpendDay) {
              let daysInCurrentCycle = 0;
              if (currentDay.date() >= cycleStartDate)
                daysInCurrentCycle = currentDay.daysInMonth();
              else daysInCurrentCycle = currentDay.clone().subtract(1, 'month').daysInMonth();
              //full cycle is included in date range
              if (lastSpendDay.diff(currentDay, 'days') + 1 > daysInCurrentCycle) {
                spendTotal += Number(cost);
                currentDay.add(daysInCurrentCycle, 'days');
              }
              //partial cycle is in date range
              else {
                spendTotal +=
                  (Number(cost) / daysInCurrentCycle) * (lastSpendDay.diff(currentDay, 'days') + 1);
                currentDay.add(lastSpendDay.diff(currentDay, 'days') + 1, 'day');
              }
            }
          }
        }
        //recurring every 2 or greater months - not quite accurate, but leaving it for now

        //TODO multi month recurring - partial last cycle
        else if (monthlyRecurring && Number(recurringIntervalLength) > 1) {
          const totalMonths = (lastSpendDay.diff(firstSpendDay, 'days') + 1) / 30.5;
          spendTotal += Number(cost) * (totalMonths / recurringIntervalLength);
        }
        //non-recurring, or recurring daily and no end date
        else spendTotal += avgDailyCost * (lastSpendDay.diff(firstSpendDay, 'days') + 1);

        spendTotal -= lastDayReduction;

        //metric based spend
      } else {
        const isRelevant = (x) =>
          momentNoTimezone(x.start)?.isSameOrAfter(firstSpendDay, 'day') &&
          momentNoTimezone(x.start)?.isSameOrBefore(lastSpendDay, 'day');
        const totalRelevantSales =
          total(
            newStats['shopify']?.filter((x) => isRelevant(x)),
            'total_price'
          ) ?? 0;
        const totalRelevantRefunds =
          -total(
            newStats['shopify']?.filter((x) => isRelevant(x)),
            'total_refund'
          ) ?? 0;

        if (metricId == 'sales') {
          spendTotal += (totalRelevantSales * percent) / 100;
        }
        if (metricId == 'facebookAds') {
          const totalRelevantFacebook =
            total(
              newStats['facebook-ads']?.filter((x) => isRelevant(x)),
              'spend'
            ) ?? 0;
          spendTotal += (totalRelevantFacebook * percent) / 100;
        }
        if (metricId == 'googleAds') {
          const totalRelevantGoogle =
            total(
              newStats['google-ads']?.filter((x) => isRelevant(x)),
              'spend'
            ) ?? 0;
          spendTotal += (totalRelevantGoogle * percent) / 100;
        }
        if (metricId == 'snapchatAds') {
          const totalRelevantSnapchat =
            total(
              newStats['snapchat-ads']?.filter((x) => isRelevant(x)),
              'spend'
            ) ?? 0;
          spendTotal += (totalRelevantSnapchat * percent) / 100;
        }
        if (metricId == 'tiktokAds') {
          const totalRelevantTiktok =
            total(
              newStats['tiktok-ads']?.filter((x) => isRelevant(x)),
              'spend'
            ) ?? 0;
          spendTotal += (totalRelevantTiktok * percent) / 100;
        }

        if (metricId == 'twitterAds') {
          const totalRelevantTwitter =
            total(
              newStats['twitter-ads']?.filter((x) => isRelevant(x)),
              'spend'
            ) ?? 0;
          spendTotal += (totalRelevantTwitter * percent) / 100;
        }

        if (metricId == 'bingAds') {
          const totalRelevantBing =
            total(
              newStats['bing']?.filter((x) => isRelevant(x)),
              'spend'
            ) ?? 0;
          spendTotal += (totalRelevantBing * percent) / 100;
        }
        if (metricId == 'pinterestAds') {
          const totalRelevantPinterest =
            total(
              newStats['pinterest-ads']?.filter((x) => isRelevant(x)),
              'spend'
            ) ?? 0;
          spendTotal += (totalRelevantPinterest * percent) / 100;
        }
        if (metricId == 'blendedAds') {
          const totalRelevantFacebook =
            total(
              newStats['facebook-ads']?.filter((x) => isRelevant(x)),
              'spend'
            ) ?? 0;
          const totalRelevantGoogle =
            total(
              newStats['google-ads']?.filter((x) => isRelevant(x)),
              'spend'
            ) ?? 0;
          const totalRelevantSnapchat =
            total(
              newStats['snapchat-ads']?.filter((x) => isRelevant(x)),
              'spend'
            ) ?? 0;
          const totalRelevantTiktok =
            total(
              newStats['tiktok-ads']?.filter((x) => isRelevant(x)),
              'spend'
            ) ?? 0;
          const totalRelevantTwitter =
            total(
              newStats['twitter-ads']?.filter((x) => isRelevant(x)),
              'spend'
            ) ?? 0;
          const totalRelevantPinterest =
            total(
              newStats['pinterest-ads']?.filter((x) => isRelevant(x)),
              'spend'
            ) ?? 0;
          const totalRelevantBing =
            total(
              newStats['bing']?.filter((x) => isRelevant(x)),
              'spend'
            ) ?? 0;
          const totalRelevantMountain =
            total(
              newStats['mountain']?.filter((x) => isRelevant(x)),
              'spend'
            ) ?? 0;
          const totalRelevantCriteo =
            total(
              newStats['criteo']?.filter((x) => isRelevant(x)),
              'spend'
            ) ?? 0;
          const totalRelevantSmsbump =
            total(
              newStats['smsbump']?.filter((x) => isRelevant(x)),
              'spend'
            ) ?? 0;
          const totalRelevantBlended =
            totalRelevantFacebook +
            totalRelevantGoogle +
            totalRelevantSnapchat +
            totalRelevantTiktok +
            totalRelevantTwitter +
            totalRelevantPinterest +
            totalRelevantBing +
            totalRelevantMountain +
            totalRelevantCriteo +
            totalRelevantSmsbump;
          spendTotal += (totalRelevantBlended * percent) / 100;
        }
        if (metricId === 'cogs') {
          const totalRelevantCogs =
            total(
              newStats['shopify']?.filter((x) => isRelevant(x)),
              'total_cost'
            ) ?? 0;
          spendTotal += (totalRelevantCogs * percent) / 100;
        }
        if (metricId === 'netSales') {
          const totalRelevantNetSales = totalRelevantSales - totalRelevantRefunds;
          spendTotal += (totalRelevantNetSales * percent) / 100;
        }
        if (metricId === 'totalRefunds') {
          spendTotal += (totalRelevantRefunds * percent) / 100;
        }
        if (metricId === 'orders') {
          const totalRelevantOrders =
            total(
              newStats['shopify']?.filter((x) => isRelevant(x)),
              'count'
            ) ?? 0;
          spendTotal += totalRelevantOrders * percent;
        }
        if (metricId === 'mountainAds') {
          const totalRelevantMountain =
            total(
              newStats['mountain']?.filter((x) => isRelevant(x)),
              'spend'
            ) ?? 0;
          spendTotal += (totalRelevantMountain * percent) / 100;
        }
        if (metricId === 'criteoAds') {
          const totalRelevantCriteo =
            total(
              newStats['criteo']?.filter((x) => isRelevant(x)),
              'spend'
            ) ?? 0;
          spendTotal += (totalRelevantCriteo * percent) / 100;
        }
        if (metricId === 'smsbump') {
          const totalRelevantSmsbump =
            total(
              newStats['smsbump']?.filter((x) => isRelevant(x)),
              'spend'
            ) ?? 0;
          spendTotal += (totalRelevantSmsbump * percent) / 100;
        }
      }
    }
  });
  return spendTotal;
};
const calculateHourlyMetricSpends = (spends, stats) => {
  const hourlySpend = {};
  for (let i = 0; i < 24; i++) {
    hourlySpend[i] = 0;
  }
  spends.forEach((spend) => {
    const { metricId, percent, id } = spend;
    switch (metricId) {
      case 'facebookAds':
        if (stats?.['facebook-ads']?.[0]?.hours === undefined) break;
        Object.entries(stats?.['facebook-ads']?.[0]?.hours).forEach(([key, val]) => {
          hourlySpend[key] += (val?.['spend'] * percent) / 100;
        });
        break;
      case 'googleAds':
        if (stats?.['google-ads']?.[0]?.hours === undefined) break;
        Object.entries(stats?.['google-ads']?.[0]?.hours).forEach(([key, val]) => {
          hourlySpend[key] += (val?.['spend'] * percent) / 100;
        });
        break;
      case 'snapchatAds':
        if (stats?.['snapchat-ads']?.[0]?.hours === undefined) break;
        Object.entries(stats?.['snapchat-ads']?.[0]?.hours).forEach(([key, val]) => {
          hourlySpend[key] += (val?.['spend'] * percent) / 100;
        });
        break;
      case 'tiktokAds':
        if (stats?.['tiktok-ads']?.[0]?.hours === undefined) break;
        Object.entries(stats?.['tiktok-ads']?.[0]?.hours).forEach(([key, val]) => {
          hourlySpend[key] += (val?.['spend'] * percent) / 100;
        });
        break;
      case 'twitterAds':
        if (stats?.['twitter-ads']?.[0]?.hours === undefined) break;
        Object.entries(stats?.['twitter-ads']?.[0]?.hours).forEach(([key, val]) => {
          hourlySpend[key] += (val?.['spend'] * percent) / 100;
        });
        break;
      case 'bingAds':
        if (stats?.['bing']?.[0]?.hours === undefined) break;
        Object.entries(stats?.['bing']?.[0]?.hours).forEach(([key, val]) => {
          hourlySpend[key] += (val?.['spend'] * percent) / 100;
        });
        break;
      case 'pinterestAds':
        if (stats?.['pinterest-ads']?.[0]?.hours === undefined) break;
        Object.entries(stats?.['pinterest-ads']?.[0]?.hours).forEach(([key, val]) => {
          hourlySpend[key] += (val?.['spend'] * percent) / 100;
        });
        break;
      case 'sales':
        if (stats?.['shopify']?.[0]?.hours === undefined) break;
        Object.entries(stats?.['shopify']?.[0]?.hours).forEach(([key, val]) => {
          hourlySpend[key] += (val?.['total_price'] * percent) / 100;
        });
        break;
      case 'orders':
        if (stats?.['shopify']?.[0]?.hours === undefined) break;
        Object.entries(stats?.['shopify']?.[0]?.hours).forEach(([key, val]) => {
          hourlySpend[key] += val?.['count'] * percent;
        });
        break;
      case 'totalRefunds':
        if (stats?.['shopify']?.[0]?.hours === undefined) break;
        Object.entries(stats?.['shopify']?.[0]?.hours).forEach(([key, val]) => {
          hourlySpend[key] += ((val?.['total_refund'] * percent) / 100) * -1;
        });
        break;
      case 'netSales':
        if (stats?.['shopify']?.[0]?.hours === undefined) break;
        Object.entries(stats?.['shopify']?.[0]?.hours).forEach(([key, val]) => {
          hourlySpend[key] += ((val?.['total_price'] + val?.['total_refund']) * percent) / 100;
        });
        break;
      case 'cogs':
        if (stats?.['shopify']?.[0]?.hours === undefined) break;
        Object.entries(stats?.['shopify']?.[0]?.hours).forEach(([key, val]) => {
          hourlySpend[key] += (val?.['total_cost'] * percent) / 100;
          return;
        });
        break;
      case 'blendedAds':
        Object.keys(hourlySpend).forEach((hour) => {
          const totalBlendedHourly =
            (stats['facebook-ads']?.[0]?.['hours']?.[hour]?.['spend'] ?? 0) +
              (stats['google-ads']?.[0]?.['hours']?.[hour]?.['spend'] ?? 0) +
              (stats['snapchat-ads']?.[0]?.['hours']?.[hour]?.['spend'] ?? 0) +
              (stats['tiktok-ads']?.[0]?.['hours']?.[hour]?.['spend'] ?? 0) +
              (stats['twitter-ads']?.[0]?.['hours']?.[hour]?.['spend'] ?? 0) +
              (stats['pinterest-ads']?.[0]?.['hours']?.[hour]?.['spend'] ?? 0) +
              (stats['bing']?.[0]?.['hours']?.[hour]?.['spend'] ?? 0) ??
            0 + (stats['mountain']?.[0]?.['hours']?.[hour]?.['spend'] ?? 0);
          stats['criteo']?.[0]?.['hours']?.[hour]?.['spend'] ?? 0;
          hourlySpend[hour] += (totalBlendedHourly * percent) / 100;
        });
        break;
      case 'mountainAds':
        if (stats?.['mountain']?.[0]?.hours === undefined) break;
        Object.entries(stats?.['mountain']?.[0]?.hours).forEach(([key, val]) => {
          hourlySpend[key] += (val?.['spend'] * percent) / 100;
        });
        break;
      case 'criteoAds':
        if (stats?.['criteo']?.[0]?.hours === undefined) break;
        Object.entries(stats?.['criteo']?.[0]?.hours).forEach(([key, val]) => {
          hourlySpend[key] += (val?.['spend'] * percent) / 100;
        });
        break;
      default:
        break;
    }
  });
  return hourlySpend;
};

const totalForRange = createSelector(
  [
    (newStats, serviceId: string, param: string, firstDay, lastDay) => [
      newStats,
      serviceId,
      param,
      firstDay,
      lastDay,
    ],
  ],
  ([newStats, serviceId, param, firstDay, lastDay]) => {
    const isRelevant = (x) => {
      return (
        momentNoTimezone(x.start)?.isSameOrAfter(firstDay, 'day') &&
        momentNoTimezone(x.start)?.isSameOrBefore(lastDay, 'day')
      );
    };

    return (
      total(
        newStats[serviceId]?.filter((x) => isRelevant(x)),
        param
      ) ?? 0
    );
  },
  { memoizeOptions: { equalityCheck: isEqual, maxSize: 300 } }
);

type CustomSpendBySource = {
  id: string;
  name?: string;
  customSpend?: number;
  customAdSpend?: number;
  campaigns?: CustomSpendBySource[];
};

const calculateCustomSpendBySource = (
  customSpends: any[],
  mainDatePickerSelectionRange: { start: moment.Moment; end: moment.Moment },
  newStats,
  customExpenseAttributionType,
  customExpenseSmartAttribution,
  shopTimezone: string
): { spendBySource: CustomSpendBySource[]; adSpendBySource: CustomSpendBySource[] } => {
  let spendBySource: CustomSpendBySource[] = [];
  let adSpendBySource: CustomSpendBySource[] = [];
  customSpends.forEach((spend) => {
    let total = calculateTotalCustomSpend(
      [spend],
      mainDatePickerSelectionRange,
      shopTimezone,
      newStats,
      customExpenseAttributionType,
      customExpenseSmartAttribution
    );
    if (!spend.source) {
      spend.source = 'Custom Spend without Source';
    }
    if (!spend.campaignId) {
      spend.campaignId = 'other';
    }
    if (!spend.campaignName) {
      spend.campaignName = 'Custom Spend without Campaign';
    }

    const arr = spend.isAdSpend ? adSpendBySource : spendBySource;
    const property = spend.isAdSpend ? 'customAdSpend' : 'customSpend';

    const source = arr.find((x) => x.id === spend.source);
    if (source) {
      source[property] ? (source[property]! += total) : (source[property] = total);
      if (!source.campaigns?.length) {
        source.campaigns = [];
      }
      const campaign = source.campaigns.find((x) => x.id === spend.campaignId);
      if (campaign) {
        campaign[property] ? (campaign[property]! += total) : (campaign[property] = total);
      } else {
        source.campaigns.push({
          id: spend.campaignId,
          name: spend.campaignName,
          [property]: total,
        });
      }
    } else {
      arr.push({
        id: spend.source,
        [property]: total,
        campaigns: [
          {
            id: spend.campaignId,
            name: spend.campaignName,
            [property]: total,
          },
        ],
      });
    }
  });

  return { spendBySource, adSpendBySource };
};

const customSpendBySource = createSelector(
  [
    getCustomSpends,
    getMainDatePickerSelectionRange,
    getNewStats,
    getCustomExpenseAttributionType,
    getCustomExpenseSmartAttribution,
    timezone,
    (state, dateRange?: { start: moment.Moment; end: moment.Moment }) => dateRange,
  ],
  (
    customSpends,
    mainDatePickerSelectionRange,
    newStats,
    customExpenseAttributionType,
    customExpenseSmartAttribution,
    shopTimezone,
    dateRange
  ) => {
    return calculateCustomSpendBySource(
      customSpends,
      dateRange || mainDatePickerSelectionRange,
      newStats,
      customExpenseAttributionType,
      customExpenseSmartAttribution,
      shopTimezone
    );
  }
);

const totalCustomSpendsByCategory = createSelector(
  [
    getCustomSpends,
    getCustomSpendCategories,
    getMainDatePickerSelectionRange,
    getNewStats,
    getCustomExpenseAttributionType,
    getCustomExpenseSmartAttribution,
    timezone,
  ],
  (
    customSpends,
    categories,
    mainDatePickerSelectionRange,
    newStats,
    customExpenseAttributionType,
    customExpenseSmartAttribution,
    shopTimezone
  ) => {
    let spendByCategory = {};
    categories.forEach((category) => {
      let filteredSpends = customSpends.filter((cs) => cs.category === category);
      spendByCategory[category] = {};
      filteredSpends.forEach((spend) => {
        let total = calculateTotalCustomSpend(
          [spend],
          mainDatePickerSelectionRange,
          shopTimezone,
          newStats,
          customExpenseAttributionType,
          customExpenseSmartAttribution
        );
        spendByCategory[category][spend.title] = Math.round(total);
      });
      let categoryTotal = calculateTotalCustomSpend(
        filteredSpends,
        mainDatePickerSelectionRange,
        shopTimezone,
        newStats,
        customExpenseAttributionType,
        customExpenseSmartAttribution
      );
      spendByCategory[category]['total'] = Math.round(categoryTotal);
    });
    let filteredOtherSpends = customSpends.filter((cs) => !cs.category);
    spendByCategory['Uncategorized'] = {};
    filteredOtherSpends.forEach((spend) => {
      let total = calculateTotalCustomSpend(
        [spend],
        mainDatePickerSelectionRange,
        shopTimezone,
        newStats,
        customExpenseAttributionType,
        customExpenseSmartAttribution
      );
      spendByCategory['Uncategorized'][spend.title] = Math.round(total);
    });
    let otherTotal = calculateTotalCustomSpend(
      filteredOtherSpends,
      mainDatePickerSelectionRange,
      shopTimezone,
      newStats,
      customExpenseAttributionType,
      customExpenseSmartAttribution
    );
    spendByCategory['Uncategorized']['total'] = Math.round(otherTotal) || 0;

    return spendByCategory;
  }
);

const total_product_cost = createSelector([getShopifyStats], (stats) => {
  return total(stats, 'total_cost');
});

const total_handling_fees = createSelector([getShopifyStats], (stats) => {
  return totalWithAlternative(stats, 'total_handling_fees', 'total_fees');
});

const newCustomerSales = createSelector([getShopifyStats], (stats) => {
  return total(stats, 'new_customers_total_price');
});

const googleTransactions = createSelector([getStats], (stats) => total(stats, 'ga_transactions'));

const new_customers = createSelector([getShopifyStats], (stats) => {
  return total(stats, 'new_customers_count');
});

const newCustomersPercent = createSelector(
  [new_customers, totalOrders],
  (new_customers, totalOrders) => (!totalOrders ? 0 : (100 * new_customers) / totalOrders)
);

const oldCustomersPercent = createSelector([newCustomersPercent], (newCustomersPercent) => {
  var val = 100 - newCustomersPercent;
  if (val < 0) val = 0;
  return val;
});

const newCustomersOrders = createSelector([new_customers], (new_customers) =>
  !new_customers ? 0 : new_customers
);

const totalRefunds = createSelector([getShopifyStats], (stats) => {
  return -total(stats, 'total_refund');
});

const netSales = createSelector(
  [totalSales, totalRefunds],
  (totalSales, totalRefunds) => totalSales - totalRefunds
);

const totalReturns = createSelector([totalRefunds, totalSales], (totalRefunds, totalSales) =>
  totalSales ? (100 * totalRefunds) / totalSales : 0
);

const totalPaymentGatewayCosts = createSelector([getShopifyStats], (stats) =>
  total(stats, 'total_payment_gateway_costs')
);

const totalProductCostsOrders = createSelector([getShopifyStats], (stats) =>
  total(stats, 'total_cost')
);

const totalCostsRefunds = createSelector([getShopifyStats, totalSales], (stats, sales) => {
  return total(stats, 'total_cost_refund');
});

const totalProductCosts = createSelector(
  [totalProductCostsOrders, totalCostsRefunds],
  (totalProductCostsOrders, totalCostsRefunds) => totalProductCostsOrders + totalCostsRefunds
);

const totalHandlingFees = createSelector([getShopifyStats], (stats) =>
  totalWithAlternative(stats, 'total_handling_fees_orders', 'total_fees')
);

const totalShippingCosts = createSelector(
  [getNewStats, getActiveShippingProvider, useShippingChargesAsCost],
  (stats, providerId, useShippingChargesAsCost) => {
    if (useShippingChargesAsCost) return total(stats['shopify'], 'total_shipping_price');
    return total(stats[providerId], shippingCostPropPerProvider[providerId]);
  }
);

const totalShippingPrice = createSelector([getShopifyStats], (stats) => {
  return total(stats, 'total_shipping_price');
});

// const totalShippingTax = createSelector([getShopifyStats], (stats) => {
//   return total(stats, 'total_shipping_tax');
// });

const totalTaxes = createSelector([getShopifyStats], (stats) => {
  return total(stats, 'total_tax');
});

const totalRefundTaxes = createSelector([getShopifyStats], (stats) => {
  return total(stats, 'tax_refund');
});

const totalNetTaxes = createSelector(
  [totalTaxes, totalRefundTaxes],
  (totalTaxes = 0, totalRefundTaxes = 0) => totalTaxes + totalRefundTaxes
);

const chartTaxes = createSelector([getShopifyStats], (stats) => {
  return simpleChart(stats, 'total_tax');
});

const chartNetTaxes = createSelector([getShopifyStats], (stats) => {
  return expressionChart(
    stats,
    ['total_tax', 'tax_refund'],
    ([total_tax, tax_refund]) => (total_tax ?? 0) + (tax_refund ?? 0)
  );
});

const totalDiscounts = createSelector([getShopifyStats], (stats) => {
  return total(stats, 'total_discounts');
});

const totalDiscountsChart = createSelector([getShopifyStats], (stats) => {
  return simpleChart(stats, 'total_discounts');
});

const chartNewCustomerSales = createSelector([getShopifyStats], (stats) => {
  return simpleChart(stats, 'new_customers_total_price');
});

const chartReturningCustomerSales = createSelector(
  [chartSales, chartNewCustomerSales],
  (chartSales, chartNewCustomerSales) => simpleChartMinus(chartSales, chartNewCustomerSales)
);

const chartRefunds = createSelector([getShopifyStats], (stats) => {
  return simpleChart(stats, 'total_refund');
});

const totalReturnsChart = createSelector(
  [chartRefunds, chartSales, totalSales],
  (chartRefunds, chartSales, totalSales) => {
    if (!totalSales) return [];
    return simpleChart2(chartRefunds, chartSales, { chart1yMultiplier: -100 });
  }
);

const netSalesChart = createSelector([chartSales, chartRefunds], (chartSales, chartRefunds) =>
  simpleChartPlus(chartSales, chartRefunds)
);

const chartNewCustomersPercent = createSelector([getShopifyStats], (stats) => {
  stats = stats || emptyArray;

  const customerField = 'new_customers_count';
  const totalPriceField = 'count';

  var chart = [];
  var lastYearDays;
  var firstStatDay;
  var isOneDay = stats.length == 1;
  if (stats[0]) {
    lastYearDays = moment(stats[0].start).endOf('year').dayOfYear();
    firstStatDay = moment(stats[0].start).dayOfYear();
  }

  stats.forEach((day) => {
    if (isOneDay) {
      chart = Array.from({ length: 24 }).map((_, i) => ({ x: Number(i), y: 0 }));
      Object.entries(day.hours).forEach(([key, hour]) => {
        if (hour[customerField]) {
          var totalOrders = Number(hour[totalPriceField]);
          var y = !totalOrders ? 0 : (100 * Number(hour[customerField])) / totalOrders;
          chart.find((i) => i.x == Number(key)).y = y;
        }
      });
    } else {
      let x = moment(day.start).dayOfYear();
      if (x < firstStatDay) x += lastYearDays;
      var dayTotal = 0;
      Object.values(day.hours).forEach((hour) => {
        if (hour[customerField]) {
          var totalOrders = Number(hour[totalPriceField]);
          var y = !totalOrders ? 0 : (100 * Number(hour[customerField])) / totalOrders;
          dayTotal = y;
        }
      });

      chart.push({ x, y: dayTotal });
    }
  });

  return chart;
});

const chartOldCustomersPercent = createSelector([getShopifyStats], (stats) => {
  stats = stats || emptyArray;
  const customerField = 'new_customers_count';
  const totalPriceField = 'total_price';

  let chart = [];
  let lastYearDays;
  let firstStatDay;
  let isOneDay = stats.length == 1;
  if (stats[0]) {
    lastYearDays = moment(stats[0].start).endOf('year').dayOfYear();
    firstStatDay = moment(stats[0].start).dayOfYear();
  }

  stats.forEach((day) => {
    if (isOneDay) {
      chart = Array.from({ length: 24 }).map((_, i) => ({ x: Number(i), y: 0 }));
      Object.entries(day.hours).forEach(([key, hour]) => {
        if (hour[customerField]) {
          const totalOrders = Number(hour[totalPriceField]);
          const y = !totalOrders
            ? 0
            : 100 - 100 * ((100 * Number(hour[customerField])) / totalOrders);
          chart.find((i) => i.x == Number(key)).y = y;
        }
      });
    } else {
      let x = moment(day.start).dayOfYear();
      if (x < firstStatDay) x += lastYearDays;
      let dayTotal = 0;
      Object.values(day.hours).forEach((hour) => {
        if (hour[customerField]) {
          const totalOrders = Number(hour[totalPriceField]);
          const y = !totalOrders
            ? 0
            : 100 - 100 * ((100 * Number(hour[customerField])) / totalOrders);
          dayTotal = y;
        }
      });

      chart.push({ x, y: dayTotal });
    }
  });

  return chart;
});

const chartNewCustomersOrders = createSelector([getShopifyStats], (stats) => {
  return simpleChart(stats, 'new_customers_count');
});

const totalGoogleAnalyticsUsers = createSelector([getStats], (stats) => total(stats, 'ga_users'));

const totalGoogleAnalyticsSessions = createSelector([getStats], (stats) =>
  total(stats, 'ga_sessions')
);

const totalGoogleAnalyticsBounces = createSelector([getStats], (stats) =>
  total(stats, 'ga_bounces')
);

const googleBounceRate = createSelector(
  [totalGoogleAnalyticsBounces, totalGoogleAnalyticsSessions],
  (bounces, sessions) => (!sessions ? 0 : (100 * bounces) / sessions)
);

const totalRevenuePerSession = createSelector(
  [totalSales, totalGoogleAnalyticsSessions],
  (totalSales, totalGoogleAnalyticsSessions) =>
    !totalGoogleAnalyticsSessions ? 0 : totalSales / totalGoogleAnalyticsSessions
);

const totalRevenuePerSessionChart = createSelector(
  [chartSales, totalGoogleAnalyticsSessions, getStats],
  (chartSales, totalGoogleAnalyticsSessions, stats) => {
    if (!totalGoogleAnalyticsSessions) return [];
    var totalGoogleAnalyticsSessionsChart = simpleChart(stats, 'ga_sessions');
    return simpleChart2(chartSales, totalGoogleAnalyticsSessionsChart);
  }
);

const averageGaTransactionsPerSession = createSelector(
  [totalGoogleAnalyticsSessions, googleTransactions],
  (totalGoogleAnalyticsSessions, googleTransactions) => {
    return !totalGoogleAnalyticsSessions
      ? 0
      : (googleTransactions * 100) / totalGoogleAnalyticsSessions;
  }
);

const googleTransactionRevenue = createSelector([getStats], (stats) =>
  total(stats, 'ga_transactionRevenue')
);

// Important GA AOV - deprecated it's here only because old store may use it somewhere
const averageGaRevenuePerTransaction = createSelector(
  [googleTransactionRevenue, googleTransactions],
  (googleTransactionRevenue, googleTransactions) =>
    !googleTransactions ? 0 : googleTransactionRevenue / googleTransactions
);

const shopifyAov = createSelector(
  [totalOrdersWithAmount, totalSales, totalTaxes, totalShippingPrice],
  (totalOrdersWithAmount, totalSales, totalTaxes, totalShippingPrice) =>
    !totalOrdersWithAmount
      ? 0
      : (totalSales - totalTaxes - totalShippingPrice) / totalOrdersWithAmount
);

const shopifyAovIncludeZero = createSelector(
  [totalOrders, totalSales, totalTaxes, totalShippingPrice],
  (totalOrders, totalSales, totalTaxes, totalShippingPrice) =>
    !totalOrders ? 0 : (totalSales - totalTaxes - totalShippingPrice) / totalOrders
);

const chartShopifyAov = createSelector([getShopifyStats], (stats) => {
  return expressionChart(
    stats,
    ['total_price', 'total_tax', 'total_shipping_price', 'orders_with_amount'],
    ([total_price, total_tax, total_shipping_price, orders_with_amount]) =>
      safeDivide(
        (total_price ?? 0) - (total_tax ?? 0) - (total_shipping_price ?? 0),
        orders_with_amount
      )
  );
});

const chartShopifyAovIncludeZero = createSelector([getShopifyStats], (stats) => {
  return expressionChart(
    stats,
    ['total_price', 'total_tax', 'total_shipping_price', 'count'],
    ([total_price, total_tax, total_shipping_price, totalOrders]) =>
      safeDivide((total_price ?? 0) - (total_tax ?? 0) - (total_shipping_price ?? 0), totalOrders)
  );
});

const shopifyConversionRate = createSelector(
  [totalGoogleAnalyticsSessions, totalOrders],
  (totalGoogleAnalyticsSessions, totalOrders) => {
    return !totalGoogleAnalyticsSessions ? 0 : (totalOrders * 100) / totalGoogleAnalyticsSessions;
  }
);

const chartGaTransactionsPerSession = createSelector([getStats], (stats) => {
  var chart1 = simpleChart(stats, 'ga_transactions');
  var chart2 = simpleChart(stats, 'ga_sessions');
  return simpleChart2(chart1, chart2, { chart1yMultiplier: 100 });
});

const chartGaRevenuePerTransaction = createSelector([getStats], (stats) => {
  var chart1 = simpleChart(stats, 'ga_transactionRevenue');
  var chart2 = simpleChart(stats, 'ga_transactions');
  return simpleChart2(chart1, chart2);
});

const chartGaUsers = createSelector([getStats], (stats) => simpleChart(stats, 'ga_users'));

const totalGorgiasTicketsCreated = createSelector([getStats], (stats) =>
  total(stats, 'gorgias_total_new_tickets_value')
);

const totalGorgiasTicketsReplied = createSelector([getStats], (stats) =>
  total(stats, 'gorgias_total_replied_tickets_value')
);

const gorgiasAverageTime = (stats, prop, divideBy60 = false) => {
  var total = 0;

  stats.forEach((day) => {
    var dayTotal = 0;
    Object.values(day.hours).forEach((hour) => {
      dayTotal += Number(hour[prop] || 0);
    });
    total += dayTotal;
  });

  return Math.floor(total / (stats.length * (divideBy60 ? 60 : 1)));
};

const averageGorgiasResponseTime = createSelector([getStats], (stats) =>
  gorgiasAverageTime(stats, 'gorgias_median_first_response_time_value', stats.length == 1)
);

const averageGorgiasResolutionTime = createSelector([getStats], (stats) =>
  gorgiasAverageTime(stats, 'gorgias_median_resolution_time_value', stats.length == 1)
);

const chartGorgiasTicketsCreated = createSelector([getStats], (stats) =>
  simpleChart(stats, 'gorgias_total_new_tickets_value')
);

const chartGorgiasTicketsReplied = createSelector([getStats], (stats) =>
  simpleChart(stats, 'gorgias_total_replied_tickets_value')
);

const chartGorgiasTime = (stats, prop) => {
  var chart = [];

  if (!stats || !stats.length) return [];
  if (stats.length === 1) {
    // one day, chart based on hours
    var y = gorgiasAverageTime(stats, prop, true);
    var x = 0; // Object.keys(stats[0].hours);

    chart.push({ x, y });
  } else {
    var lastYearDays = moment(stats[0].start).endOf('year').dayOfYear();
    var firstStatDay = moment(stats[0].start).dayOfYear();
    stats.forEach((stat) => {
      var x, y;
      x = moment(stat.start).dayOfYear();
      if (x < firstStatDay) x += lastYearDays;
      y = gorgiasAverageTime([stat], prop, false);
      chart.push({ x, y });
    });
  }

  return chart;
};

const chartGorgiasResolutionTime = createSelector([getStats], (stats) =>
  chartGorgiasTime(stats, 'gorgias_median_resolution_time_value')
);

const chartGorgiasResponseTime = createSelector([getStats], (stats) =>
  chartGorgiasTime(stats, 'gorgias_median_first_response_time_value')
);

const totalNewGrossSales = createSelector([getShopifyStats], (stats) =>
  total(stats, 'total_price_gross')
);

const chartNewGrossSales = createSelector([getShopifyStats], (stats) =>
  simpleChart(stats, 'total_price_gross')
);

export const chartCogs = createSelector([getShopifyStats], (stats) => {
  return expressionChart(
    stats,
    ['total_cost', 'total_cost_refund'],
    ([total_cost, total_cost_refund]) => (total_cost ?? 0) + (total_cost_refund ?? 0)
  );
});

const chartHandlingFees = createSelector([getShopifyStats], (stats) =>
  simpleChartWithAlternative(stats, 'total_handling_fees_orders', 'total_fees')
);

const totalGrossSales = createSelector(
  [totalSales, totalDiscounts, totalTaxes, totalShippingPrice],
  (totalSales, totalDiscounts, totalTaxes, totalShippingPrice) => {
    return totalSales + totalDiscounts - totalTaxes - totalShippingPrice;
  }
);

const getUniqueCustomerCount = createSelector(
  [getPreCalculatedUniqueCustomerCount],
  (preCalculatedUniqueCustomerCount) => preCalculatedUniqueCustomerCount
);

const uniqueCustomerLifetimeValue = createSelector(
  [totalSales, getUniqueCustomerCount],
  (totalSales, uniqueCustomerCount) =>
    uniqueCustomerCount == 0 ? 0 : totalSales / uniqueCustomerCount
);

const customerFrequency = createSelector(
  [totalOrdersWithAmount, getUniqueCustomerCount],
  (totalOrdersWithAmount, uniqueCustomerCount) =>
    uniqueCustomerCount == 0 ? 0 : totalOrdersWithAmount / uniqueCustomerCount
);

const mergeAndSumSegmentBySales = (stats, prop) => {
  const data = {};
  if (stats?.[prop]) {
    Object.entries<any>(stats[prop]).forEach(([id, obj]) => {
      const revenue = total(obj ?? [], 'total_price');
      const count = total(obj ?? [], 'count');
      data[id] = { revenue, count };
    });
  }
  return data;
};

const willyMetric = createSelector([willySummaryBox], (willySummaryWidgets) => {
  return willySummaryWidgets.reduce((acc, widget) => {
    const totalIndex = widget.queryData.data?.[0]?.value.findIndex?.((x) => x === 'total');
    let n = totalIndex > -1 ? widget.queryData.data?.[1]?.value?.[totalIndex] : 0;
    if (isNaN(n)) n = 0;
    acc[widget.queryId] = n;
    return acc;
  }, {});
});

const willyChart = createSelector(
  [willySummaryBox, getMainDatePickerSelectionRange],
  (willySummaryWidgets, { start, end }) => {
    return willySummaryWidgets.reduce((acc, widget) => {
      function spliceImmutable(arr, index) {
        return [...arr.slice(0, index), ...arr.slice(index + 1)];
      }

      const totalIndex = widget.queryData.data?.[0]?.value.findIndex?.((x) => x === 'total');
      const xsTemp = spliceImmutable(widget.queryData.data?.[0]?.value ?? [], totalIndex);
      const ysTemp = spliceImmutable(widget.queryData.data?.[1]?.value ?? [], totalIndex);

      let paired = xsTemp.map((x, i) => ({
        date: x as Date,
        value: ysTemp[i],
      }));

      paired.sort((a, b) => +new Date(a.date) - +new Date(b.date));

      let xs = paired.map((item) => item.date);
      let ys = paired.map((item) => item.value);

      if (totalIndex > -1 && Array.isArray(ys)) {
        const metricsBreakdown = convertWillyToMetricsBreakdown(xs, ys);
        const stats = convertDataToStats(
          metricsBreakdown,
          moment(start).format(),
          moment(end).format()
        );
        acc[widget.queryId] = simpleChart(stats, 'metric');
      } else {
        return {
          x: 0,
          y: 0,
        };
      }
      return acc;
    }, {});
  }
);

const order_tags = createSelector(
  [getShopifySegmentStats, () => 'order_tags'],
  mergeAndSumSegmentBySales
);

const sources = createSelector(
  [getShopifySegmentStats, () => 'sources'],
  mergeAndSumSegmentBySales
);

const orderTagsCharts = createSelector([getShopifySegmentStats], (stats) =>
  salesBySegmentChart(stats, 'order_tags')
);

const sourcesCharts = createSelector([getShopifySegmentStats], (stats) =>
  salesBySegmentChart(stats, 'sources')
);

const blendedAdsWithoutCustom = createSelector(
  [
    totalFacebookInsights,
    totalGoogleAdsCost,
    totalSnapchatSpend,
    tiktokSpend,
    pinterestSpend,
    influencerSpend,
    twitterSpend,
    bingAdSpend,
    mountainSpend,
    criteoSpend,
    smsbumpSpend,
  ],
  (
    totalFacebookInsights,
    totalGoogleAdsCost,
    totalSnapchatSpend,
    tiktokSpend,
    pinterestSpend,
    influencerSpend,
    twitterSpend,
    bingAdSpend,
    mountainSpend,
    criteoSpend,
    smsbumpSpend
  ) => {
    var totalAdsCosts =
      totalFacebookInsights +
      totalGoogleAdsCost +
      totalSnapchatSpend +
      tiktokSpend +
      pinterestSpend +
      influencerSpend +
      twitterSpend +
      bingAdSpend +
      mountainSpend +
      criteoSpend +
      smsbumpSpend;
    return totalAdsCosts;
  }
);

export function sumNewStats(stats) {
  return stats.reduce((acc, day) => {
    const metrics = day.hours[0];
    Object.entries(metrics).forEach(([key, val]) => {
      if (!acc[key]) {
        acc[key] = 0;
      }
      acc[key] += val;
    });
    return acc;
  }, {});
}

export const groupStatsInternal = (stats: any, groupBy: Exclude<Granularity, 'total'>) => {
  if (!stats || !stats.length) {
    return stats;
  }
  if (!groupBy || groupBy === 'hour' || groupBy === 'day') {
    return stats;
  }
  const granularity = groupBy === 'week' ? 'isoWeek' : groupBy;

  //const LOG_LABEL = 'calculate-stats-internal';
  //console.time(LOG_LABEL);

  const allData = [];
  let firstDay = stats[0].start;
  let firstDayMoment = moment(firstDay);
  let nextDay = null;

  do {
    // eslint-disable-next-line no-loop-func
    const daysInSameGroup = stats.filter((stat) => {
      stat.start = new Date(stat.start);
      if (granularity === 'month') {
        // performance optimization for month for big stores
        return (
          stat.start.getMonth() === firstDayMoment.month() &&
          stat.start.getFullYear() === firstDayMoment.year()
        );
      } else {
        return moment(stat.start).isSame(firstDayMoment, granularity);
      }
    });
    const momentTime = moment(daysInSameGroup[0].start);
    const hours = sumNewStats(daysInSameGroup);
    const id = momentTime.format('LL');
    const start = momentTime.startOf('day').format();
    const end = moment(daysInSameGroup[daysInSameGroup.length - 1].end)
      .endOf('day')
      .format();
    allData.push({ hours: { 0: hours }, id, start, end });

    // eslint-disable-next-line no-loop-func
    nextDay = stats.find((stat) => moment(stat.start).isAfter(firstDayMoment, granularity));
    firstDay = nextDay && nextDay.start;
    firstDayMoment = moment(firstDay);
  } while (firstDay);

  //console.timeEnd(LOG_LABEL);

  return allData;
};

const customAdSpends = createSelector([getCustomSpends], (customSpends) =>
  customSpends.filter((x) => x.isAdSpend)
);

// const totalCustomAdSpends = createSelector(
//   [
//     customAdSpends,
//     getMainDatePickerSelectionRange,
//     timezone,
//     getNewStats,
//     getCustomExpenseAttributionType,
//     getCustomExpenseSmartAttribution,
//     getCustomExpenseCogs,
//   ],
//   calculateTotalCustomSpend
// );

// const totalCustomAdSpendsChart = createSelector(
//   [totalCustomAdSpends, getMainDatePickerSelectionRange, timezone],
//   (totalCustomAdSpends, getMainDatePickerSelectionRange, timezone) => {
//     if (!totalCustomAdSpends) return [];
//     if (!getMainDatePickerSelectionRange) return [];
//     if (!timezone) return [];

//     moment.tz.setDefault(timezone);

//     const { start = moment(), end = moment() } = getMainDatePickerSelectionRange || {};
//     const isOneDay = start.isSame(end, 'day');
//     const firstStatDay = isOneDay ? 0 : moment(start).dayOfYear() || 1;

//     const divider = (isOneDay ? 24 : moment(end).diff(start, 'days')) || 0;
//     const values = distributeInteger(totalCustomAdSpends, divider);
//     const chart = values.map((y, i) => ({ x: firstStatDay + i, y }));
//     return chart;
//   }
// );

const totalCustomAdSpendsArr = createSelector(
  [
    isServer,
    customAdSpends,
    getMainDatePickerSelectionRange,
    timezone,
    getNewStats,
    getCustomExpenseAttributionType,
    getCustomExpenseSmartAttribution,
    (state) => state.groupStatsBy,
  ],
  (isServer, spends, range, timezone, stats, attributionType, smartAttribution, groupStatsBy) => {
    if (!range || !isServer) return;
    let allCustomSpends = [];
    let start = range.start.clone();
    const end = range.end.clone();
    if (start.isSame(end, 'day')) {
      let hours = {};
      const spendTotal = calculateTotalCustomSpend(
        spends.filter((x) => !x.metricId),
        {
          start: moment(start).tz(timezone),
          end: moment(start).tz(timezone),
        },
        timezone,
        stats,
        'daily', //need the full daily spend and will calculate the hourly spend later
        smartAttribution
      );
      const metricTotal = calculateHourlyMetricSpends(
        spends.filter(
          (x) =>
            x.metricId &&
            moment(x.start_date).tz(timezone)?.isSameOrBefore(start, 'day') &&
            (!x.end_date || moment(x.end_date).tz(timezone)?.isSameOrAfter(start, 'day'))
        ),
        stats
      );
      const now = momentNoTimezone();
      let hour = now.hour();
      if (!now.isSame(start, 'day')) hour = 23;
      for (let i = 0; i <= hour; i++) {
        if (attributionType === 'hourly') {
          hours[i] = { total_spends: spendTotal / 24 + metricTotal[i] };
        } else if (attributionType === 'smart' && smartAttribution?.[i] !== undefined) {
          const currentSmartPercentage = smartAttribution?.[i] - (smartAttribution?.[i - 1] ?? 0);
          hours[i] = {
            total_spends: spendTotal * currentSmartPercentage + metricTotal[i],
          };
        } else if (attributionType === 'daily') {
          hours[i] = {
            total_spends: i == 0 ? spendTotal + metricTotal[i] : metricTotal[i],
          };
        }
      }
      allCustomSpends.push({
        start: moment(start).tz(timezone).startOf('day'),
        end: moment(start).tz(timezone).endOf('day'),
        hours,
      });
    } else {
      while (end.isSameOrAfter(start)) {
        allCustomSpends.push({
          start: moment(start).tz(timezone).startOf('day'),
          end: moment(start).tz(timezone).endOf('day'),
          hours: {
            0: {
              total_spends: calculateTotalCustomSpend(
                spends,
                {
                  start: moment(start).tz(timezone),
                  end: moment(start).tz(timezone),
                },
                timezone,
                stats,
                attributionType,
                smartAttribution
              ),
            },
          },
        });
        start = start.add(1, 'day');
      }
    }
    return groupStatsInternal(allCustomSpends, groupStatsBy);
  }
);

const totalCustomAdSpends = createSelector(
  [
    customAdSpends,
    getMainDatePickerSelectionRange,
    timezone,
    getNewStats,
    getCustomExpenseAttributionType,
    getCustomExpenseSmartAttribution,
    totalCustomAdSpendsArr,
    isServer,
  ],
  (
    customAdSpends,
    getMainDatePickerSelectionRange,
    timezone,
    getNewStats,
    getCustomExpenseAttributionType,
    getCustomExpenseSmartAttribution,
    totalCustomAdSpendsArr,
    isServer
  ) => {
    if (isServer) {
      return total(totalCustomAdSpendsArr, 'total_spends');
    } else {
      return calculateTotalCustomSpend(
        customAdSpends,
        getMainDatePickerSelectionRange,
        timezone,
        getNewStats,
        getCustomExpenseAttributionType,
        getCustomExpenseSmartAttribution
      );
    }
  },
  lodashMemoize
);

const totalCustomAdSpendsChart = createSelector(
  [
    totalCustomAdSpends,
    getMainDatePickerSelectionRange,
    timezone,
    totalCustomAdSpendsArr,
    isServer,
  ],
  (
    totalCustomAdSpends,
    getMainDatePickerSelectionRange,
    timezone,
    totalCustomAdSpendsArr,
    isServer
  ) => {
    if (isServer) {
      return simpleChart(totalCustomAdSpendsArr, 'total_spends');
    } else {
      if (!totalCustomAdSpends) return [];
      if (!getMainDatePickerSelectionRange) return [];
      if (!timezone) return [];
      moment.tz.setDefault(timezone);
      const { start = moment(), end = moment() } = getMainDatePickerSelectionRange || {};
      const isOneDay = start.isSame(end, 'day');
      const firstStatDay = isOneDay ? 0 : moment(start).dayOfYear() || 1;
      const divider = (isOneDay ? 24 : moment(end).diff(start, 'days') + 1) || 1;
      const values = distributeInteger(totalCustomAdSpends, divider);
      const chart = values.map((y, i) => ({ x: firstStatDay + i, y }));
      return chart;
    }
  }
);

const blendedAds = createSelector(
  [blendedAdsWithoutCustom, totalCustomAdSpends, selectedStatsServices, amazonAds],
  (blendedAdsWithoutCustom, totalCustomAdSpends, selectedStatsServices, amazonAds) => {
    var totalAdsCosts = blendedAdsWithoutCustom + totalCustomAdSpends;
    if (selectedStatsServices.includes('amazon')) {
      totalAdsCosts += amazonAds;
    }
    return totalAdsCosts;
  }
);

const mer = createSelector(
  [totalSales, blendedAds, selectedStatsServices, totalAmazonProductItemPrice],
  (totalSales, blendedAds, selectedStatsServices, totalAmazonProductItemPrice) => {
    if (selectedStatsServices.includes('amazon')) {
      totalSales = (totalSales || 0) + (totalAmazonProductItemPrice || 0);
    }

    if (!totalSales) return 0;
    return (blendedAds * 100) / totalSales;
  }
);

const totalRoas = createSelector(
  [totalSales, blendedAds, selectedStatsServices, totalAmazonProductItemPrice],
  (totalSales, blendedAds, selectedStatsServices, totalAmazonProductItemPrice) => {
    if (selectedStatsServices.includes('amazon')) {
      totalSales = (totalSales || 0) + (totalAmazonProductItemPrice || 0);
    }

    if (!blendedAds) return 0;
    return +(totalSales / blendedAds).toFixed(2);
  }
);

const shopifyCpa = createSelector(
  [blendedAds, totalOrdersWithAmount],
  (blendedAds, totalOrdersWithAmount) => {
    if (!totalOrdersWithAmount) return 0;
    return blendedAds / totalOrdersWithAmount;
  }
);

const chartBlendedAds = createSelector(
  [
    chartFacebookInsights,
    chartGoogleAds,
    chartSnapchatAds,
    tiktokSpendChart,
    twitterSpendChart,
    pinterestSpendChart,
    totalCustomAdSpendsChart,
    influencerSpendChart,
    selectedStatsServices,
    chartAmazonAds,
    bingAdSpendChart,
    mountainSpendChart,
    criteoSpendChart,
    smsbumpSpendChart,
  ],
  (
    chartFacebookInsights,
    chartGoogleAds,
    chartSnapchatAds,
    tiktokSpendChart,
    twitterSpendChart,
    pinterestSpendChart,
    totalCustomAdSpendsChart,
    influencerSpendChart,
    selectedStatsServices,
    chartAmazonAds,
    bingAdSpendChart,
    mountainSpendChart,
    criteoSpendChart,
    smsbumpSpendChart
  ) => {
    const chartArr = [];
    let chart = [];
    if (chartFacebookInsights.length) {
      chartArr.push(chartFacebookInsights);
    }
    if (chartGoogleAds.length) {
      chartArr.push(chartGoogleAds);
    }
    if (chartSnapchatAds.length) {
      chartArr.push(chartSnapchatAds);
    }
    if (tiktokSpendChart.length) {
      chartArr.push(tiktokSpendChart);
    }
    if (twitterSpendChart.length) {
      chartArr.push(twitterSpendChart);
    }
    if (pinterestSpendChart.length) {
      chartArr.push(pinterestSpendChart);
    }
    if (totalCustomAdSpendsChart.length) {
      chartArr.push(totalCustomAdSpendsChart);
    }
    if (influencerSpendChart.length) {
      chartArr.push(influencerSpendChart);
    }
    if (bingAdSpendChart.length) {
      chartArr.push(bingAdSpendChart);
    }
    if (selectedStatsServices.includes('amazon') && chartAmazonAds.length) {
      chartArr.push(chartAmazonAds);
    }
    if (mountainSpendChart.length) {
      chartArr.push(mountainSpendChart);
    }
    if (criteoSpendChart.length) {
      chartArr.push(criteoSpendChart);
    }
    if (smsbumpSpendChart.length) {
      chartArr.push(smsbumpSpendChart);
    }
    if (chartArr.length === 1) {
      chart = chartArr[0];
    } else if (chartArr.length === 2) {
      chart = simpleChart3(chartArr[0], chartArr[1]);
    } else if (chartArr.length > 2) {
      chart = simpleChart3(chartArr[0], chartArr[1], chartArr.slice(2));
    }
    return chart;
  }
);

const merChart = createSelector([chartSales, chartBlendedAds], (chartSales, chartBlendedAds) => {
  if (!chartSales) return [];
  return simpleChart2(chartBlendedAds, chartSales, { chart1yMultiplier: 100 });
});

const chartShopifyCpa = createSelector(
  [chartBlendedAds, chartOrdersWithAmount],
  (chartBlendedAds, chartOrdersWithAmount) => {
    return simpleChart2(chartBlendedAds, chartOrdersWithAmount);
  }
);

const chartRoas = createSelector(
  [chartSales, chartBlendedAds, selectedStatsServices, chartAmazonProductItemPrice],
  (chartSales, chartBlendedAds, selectedStatsServices, chartAmazonProductItemPrice) => {
    try {
      let chart1 = chartSales;
      if (selectedStatsServices.includes('amazon')) {
        if (chartSales?.length && chartAmazonProductItemPrice?.length) {
          chart1 = simpleChartPlus(chartSales, chartAmazonProductItemPrice);
        } else if (chartAmazonProductItemPrice?.length) {
          chart1 = chartAmazonProductItemPrice;
        }
      }
      const c = simpleChart2(chart1, chartBlendedAds);
      return c;
    } catch (e) {
      console.warn('Chart crash:', 'chartRoas');
      return [];
    }
  }
);

const totalCpa = createSelector(
  [totalOrdersWithAmount, blendedAds, selectedStatsServices, totalAmazonOrders],
  (totalOrdersWithAmount, blendedAds, selectedStatsServices, totalAmazonOrders) => {
    if (selectedStatsServices.includes('amazon')) {
      totalOrdersWithAmount = (totalOrdersWithAmount || 0) + (totalAmazonOrders || 0);
    }
    if (!totalOrdersWithAmount) return 0;
    return blendedAds / totalOrdersWithAmount;
  }
);

const chartTotalCpa = createSelector(
  [chartBlendedAds, chartOrdersWithAmount, selectedStatsServices, chartAmazonOrders],
  (chartBlendedAds, chartOrdersWithAmount, selectedStatsServices, chartAmazonOrders) => {
    var chart1 = chartOrdersWithAmount;
    if (selectedStatsServices.includes('amazon')) {
      if (chart1?.length && chartAmazonOrders?.length) {
        chart1 = simpleChartPlus(chart1, chartAmazonOrders);
      } else if (chartAmazonOrders?.length) {
        chart1 = chartAmazonOrders;
      }
    }
    var chart2 = simpleChart2(chartBlendedAds, chart1, {});
    return chart2;
  }
);

const ltvCpa = createSelector([shopifyCpa, uniqueCustomerLifetimeValue], (cpa, ltv) =>
  cpa == 0 ? 0 : ltv / cpa
);

const customNonAdSpends = createSelector([getCustomSpends], (customSpends) => {
  return customSpends.filter((x) => !x.isAdSpend);
});

// const totalCustomNonAdSpends = createSelector(
//   [
//     customNonAdSpends,
//     getMainDatePickerSelectionRange,
//     timezone,
//     getNewStats,
//     getCustomExpenseAttributionType,
//     getCustomExpenseSmartAttribution,
//     getCustomExpenseCogs,
//   ],
//   calculateTotalCustomSpend
// );

// const totalCustomNonAdSpendsChart = createSelector(
//   [totalCustomNonAdSpends, getMainDatePickerSelectionRange, timezone],
//   (totalCustomNonAdSpends, getMainDatePickerSelectionRange, timezone) => {
//     if (!totalCustomAdSpends) return [];
//     if (!getMainDatePickerSelectionRange) return [];
//     if (!timezone) return [];

//     moment.tz.setDefault(timezone);

//     const { start = moment(), end = moment() } = getMainDatePickerSelectionRange || {};
//     const isOneDay = start.isSame(end, 'day');
//     const firstStatDay = isOneDay ? 0 : moment(start).dayOfYear() || 1;

//     const divider = (isOneDay ? 24 : moment(end).diff(start, 'days')) || 0;
//     const values = distributeInteger(totalCustomNonAdSpends, divider);
//     const chart = values.map((y, i) => ({ x: firstStatDay + i, y }));
//     return chart;
//   }
// );
const totalCustomNonAdSpendsArr = createSelector(
  [
    isServer,
    customNonAdSpends,
    getMainDatePickerSelectionRange,
    timezone,
    getNewStats,
    getCustomExpenseAttributionType,
    getCustomExpenseSmartAttribution,
    (state) => state.groupStatsBy,
  ],
  (isServer, spends, range, timezone, stats, attributionType, smartAttribution, groupStatsBy) => {
    if (!range || !isServer) return;
    let allCustomSpends = [];
    let start = range.start.clone();
    const end = range.end.clone();
    if (start.isSame(end, 'day')) {
      let hours = {};
      const spendTotal = calculateTotalCustomSpend(
        spends.filter((x) => !x.metricId),
        {
          start: moment(start).tz(timezone),
          end: moment(start).tz(timezone),
        },
        timezone,
        stats,
        'daily', //need the full daily spend and will calculate the hourly spend later
        smartAttribution
      );
      const metricTotal = calculateHourlyMetricSpends(
        spends.filter(
          (x) =>
            x.metricId &&
            moment(x.start_date).tz(timezone)?.isSameOrBefore(start, 'day') &&
            (!x.end_date || moment(x.end_date).tz(timezone)?.isSameOrAfter(start, 'day'))
        ),
        stats
      );
      const now = momentNoTimezone();
      let hour = now.hour();
      if (!now.isSame(start, 'day')) hour = 23;
      for (let i = 0; i <= hour; i++) {
        if (attributionType === 'hourly') {
          hours[i] = { total_spends: spendTotal / 24 + metricTotal[i] };
        } else if (attributionType === 'smart' && smartAttribution?.[i] !== undefined) {
          const currentSmartPercentage = smartAttribution?.[i] - (smartAttribution?.[i - 1] ?? 0);
          hours[i] = { total_spends: spendTotal * currentSmartPercentage + metricTotal[i] };
        } else if (attributionType === 'daily') {
          hours[i] = { total_spends: i == 0 ? spendTotal + metricTotal[i] : metricTotal[i] };
        }
      }
      allCustomSpends.push({
        start: moment(start).tz(timezone).startOf('day'),
        end: moment(start).tz(timezone).endOf('day'),
        hours,
      });
    } else {
      while (end.isSameOrAfter(start)) {
        allCustomSpends.push({
          start: moment(start).tz(timezone).startOf('day'),
          end: moment(start).tz(timezone).endOf('day'),
          hours: {
            0: {
              total_spends: calculateTotalCustomSpend(
                spends,
                {
                  start: moment(start).tz(timezone),
                  end: moment(start).tz(timezone),
                },
                timezone,
                stats,
                attributionType,
                smartAttribution
              ),
            },
          },
        });
        start = start.add(1, 'day');
      }
    }
    return groupStatsInternal(allCustomSpends, groupStatsBy);
  }
);

const totalCustomNonAdSpends = createSelector(
  [
    customNonAdSpends,
    getMainDatePickerSelectionRange,
    timezone,
    getNewStats,
    getCustomExpenseAttributionType,
    getCustomExpenseSmartAttribution,
    totalCustomNonAdSpendsArr,
    isServer,
  ],
  (
    customNonAdSpends,
    getMainDatePickerSelectionRange,
    timezone,
    getNewStats,
    getCustomExpenseAttributionType,
    getCustomExpenseSmartAttribution,
    totalCustomNonAdSpendsArr,
    isServer
  ) => {
    if (isServer) {
      return total(totalCustomNonAdSpendsArr, 'total_spends');
    } else {
      return calculateTotalCustomSpend(
        customNonAdSpends,
        getMainDatePickerSelectionRange,
        timezone,
        getNewStats,
        getCustomExpenseAttributionType,
        getCustomExpenseSmartAttribution
      );
    }
  },
  lodashMemoize
);

const totalCustomNonAdSpendsChart = createSelector(
  [
    totalCustomNonAdSpends,
    getMainDatePickerSelectionRange,
    timezone,
    totalCustomNonAdSpendsArr,
    isServer,
  ],
  (
    totalCustomNonAdSpends,
    getMainDatePickerSelectionRange,
    timezone,
    totalCustomNonAdSpendsArr,
    isServer
  ) => {
    if (isServer) {
      return simpleChart(totalCustomNonAdSpendsArr, 'total_spends');
    } else {
      if (!totalCustomNonAdSpends) return [];
      if (!getMainDatePickerSelectionRange) return [];
      if (!timezone) return [];

      moment.tz.setDefault(timezone);

      const { start = moment(), end = moment() } = getMainDatePickerSelectionRange || {};
      const isOneDay = start.isSame(end, 'day');
      const firstStatDay = isOneDay ? 0 : moment(start).dayOfYear() || 1;

      const divider = (isOneDay ? 24 : moment(end).diff(start, 'days') + 1) || 1;
      const values = distributeInteger(totalCustomNonAdSpends, divider);
      const chart = values.map((y, i) => ({ x: firstStatDay + i, y }));
      return chart;
    }
  }
);

const totalCustomSpends = createSelector(
  [totalCustomAdSpends, totalCustomNonAdSpends],
  (adSpend, nonAdSpend) => adSpend + nonAdSpend,
  lodashMemoize
);

const totalCustomSpendsChart = createSelector(
  [totalCustomAdSpendsChart, totalCustomNonAdSpendsChart],
  (adSpend, nonAdSpend) => {
    return simpleChartPlus(adSpend, nonAdSpend);
  }
);

const totalShippingCostsChart = createSelector(
  [getNewStats, getActiveShippingProvider, useShippingChargesAsCost],
  (stats, providerId, useShippingChargesAsCost) => {
    if (useShippingChargesAsCost) return simpleChart(stats['shopify'], 'total_shipping_price');
    return simpleChart(stats[providerId], shippingCostPropPerProvider[providerId]);
  }
);

const totalPaymentGatewayCostsChart = createSelector([getShopifyStats], (stats) =>
  simpleChart(stats, 'total_payment_gateway_costs')
);

const grossProfit = createSelector(
  [
    netSales,
    totalProductCosts,
    totalShippingCosts,
    totalHandlingFees,
    totalTaxes,
    totalPaymentGatewayCosts,
  ],
  (
    netSales,
    totalProductCosts,
    totalShippingCosts,
    totalHandlingFees,
    totalTaxes,
    totalPaymentGatewayCosts
  ) => {
    return (
      netSales -
      totalProductCosts -
      totalShippingCosts -
      totalHandlingFees -
      totalTaxes -
      totalPaymentGatewayCosts
    );
  }
);

const grossProfitChart = createSelector(
  [
    netSalesChart,
    chartCogs,
    totalShippingCostsChart,
    chartHandlingFees,
    chartTaxes,
    totalPaymentGatewayCostsChart,
  ],
  (
    netSalesChart,
    chartCogs,
    totalShippingCostsChart,
    chartHandlingFees,
    chartTaxes,
    totalPaymentGatewayCostsChart
  ) => {
    const moreCharts = [
      totalShippingCostsChart,
      chartHandlingFees,
      chartTaxes,
      totalPaymentGatewayCostsChart,
    ];
    return simpleChartMinus(netSalesChart, chartCogs, moreCharts);
  }
);

const poas = createSelector(
  [grossProfit, blendedAds, selectedStatsServices, amazonNetProfit],
  (grossProfit, blendedAds, selectedStatsServices, amazonNetProfit) => {
    if (selectedStatsServices.includes('amazon')) {
      grossProfit += amazonNetProfit;
    }
    return blendedAds ? grossProfit / blendedAds : 0;
  }
);

const poasChart = createSelector(
  [grossProfitChart, chartBlendedAds, selectedStatsServices, cahrtAmazonNetProfit],
  (grossProfitChart, chartBlendedAds, selectedStatsServices, chartAmazonNetProfit) => {
    let chart1 = grossProfitChart;
    if (selectedStatsServices.includes('amazon')) {
      if (chart1?.length && chartAmazonNetProfit?.length) {
        chart1 = simpleChartPlus(grossProfitChart, chartAmazonNetProfit);
      } else if (chartAmazonNetProfit?.length) {
        chart1 = chartAmazonNetProfit;
      }
    }
    return simpleChart2(chart1, chartBlendedAds);
  }
);

const shopifyNetProfit = createSelector(
  [
    totalSales,
    totalShippingCosts,
    totalPaymentGatewayCosts,
    totalHandlingFees,
    totalCustomSpends,
    totalProductCosts,
    totalRefunds,
    blendedAdsWithoutCustom,
    totalTaxes,
  ],
  (
    totalSales,
    totalShippingCosts,
    totalPaymentGatewayCosts,
    totalHandlingFees,
    totalCustomSpends,
    totalProductCosts,
    totalRefunds,
    blendedAdsWithoutCustom,
    totalTaxes
  ) => {
    const res =
      totalSales -
      totalShippingCosts -
      totalPaymentGatewayCosts -
      totalHandlingFees -
      totalCustomSpends -
      totalProductCosts -
      totalRefunds -
      blendedAdsWithoutCustom -
      totalTaxes;
    return res;
  }
);

const totalNetProfit = createSelector(
  [
    totalSales,
    totalShippingCosts,
    totalPaymentGatewayCosts,
    totalHandlingFees,
    totalCustomSpends,
    totalProductCosts,
    totalRefunds,
    blendedAdsWithoutCustom,
    totalTaxes,
    selectedStatsServices,
    amazonNetProfit,
  ],
  (
    totalSales,
    totalShippingCosts,
    totalPaymentGatewayCosts,
    totalHandlingFees,
    totalCustomSpends,
    totalProductCosts,
    totalRefunds,
    blendedAdsWithoutCustom,
    totalTaxes,
    selectedStatsServices,
    amazonNetProfit
  ) => {
    let _totalSales = totalSales;
    if (selectedStatsServices.includes('amazon')) {
      _totalSales += amazonNetProfit;
    }
    const res =
      _totalSales -
      totalShippingCosts -
      totalPaymentGatewayCosts -
      totalHandlingFees -
      totalCustomSpends -
      totalProductCosts -
      totalRefunds -
      blendedAdsWithoutCustom -
      totalTaxes;
    return res;
  }
);

const totalNetProfitChart = createSelector(
  [
    chartSales,
    totalShippingCostsChart,
    totalPaymentGatewayCostsChart,
    chartHandlingFees,
    totalCustomSpendsChart,
    chartCogs,
    chartRefunds,
    chartFacebookInsights,
    chartGoogleAds,
    chartSnapchatAds,
    tiktokSpendChart,
    bingAdSpendChart,
    twitterSpendChart,
    chartTaxes,
    selectedStatsServices,
    cahrtAmazonNetProfit,
    pinterestSpendChart,
    influencerSpendChart,
    mountainSpendChart,
    criteoSpendChart,
    smsbumpSpendChart,
  ],
  (
    chartSales,
    totalShippingCostsChart,
    totalPaymentGatewayCostsChart,
    chartHandlingFees,
    totalCustomSpendsChart,
    chartCogs,
    chartRefunds,
    chartFacebookInsights,
    chartGoogleAds,
    chartSnapchatAds,
    tiktokSpendChart,
    bingAdSpendChart,
    twitterSpendChart,
    chartTaxes,
    selectedStatsServices,
    chartAmazonNetProfit,
    pinterestSpendChart,
    influencerSpendChart,
    mountainSpendChart,
    criteoSpendChart,
    smsbumpSpendChart
  ) => {
    const moreCharts = [
      totalPaymentGatewayCostsChart,
      chartHandlingFees,
      totalCustomSpendsChart,
      chartCogs,
      chartFacebookInsights,
      chartGoogleAds,
      chartSnapchatAds,
      tiktokSpendChart,
      bingAdSpendChart,
      twitterSpendChart,
      chartTaxes,
      pinterestSpendChart,
      influencerSpendChart,
      mountainSpendChart,
      criteoSpendChart,
      smsbumpSpendChart,
    ];
    let netProfitChart = simpleChartMinus(chartSales, totalShippingCostsChart, moreCharts);
    netProfitChart = simpleChartPlus(netProfitChart, chartRefunds);
    if (selectedStatsServices.includes('amazon')) {
      netProfitChart = simpleChartPlus(netProfitChart, chartAmazonNetProfit);
    }
    return netProfitChart;
  }
);

const totalNetMargin = createSelector(
  [
    totalSales,
    totalShippingCosts,
    totalPaymentGatewayCosts,
    totalHandlingFees,
    totalCustomSpends,
    totalProductCosts,
    totalRefunds,
    blendedAdsWithoutCustom,
    totalTaxes,
    selectedStatsServices,
    totalAmazonProductItemPrice,
    amazonNetProfit,
  ],
  (
    totalSales,
    totalShippingCosts,
    totalPaymentGatewayCosts,
    totalHandlingFees,
    totalCustomSpends,
    totalProductCosts,
    totalRefunds,
    blendedAdsWithoutCustom,
    totalTaxes,
    selectedStatsServices,
    totalAmazonProductItemPrice,
    amazonNetProfit
  ) => {
    var netMarginPlus = totalSales;
    var netMarginMinus = 0;
    if (selectedStatsServices.includes('amazon')) {
      netMarginPlus = (netMarginPlus || 0) + totalAmazonProductItemPrice;
      netMarginMinus += totalAmazonProductItemPrice - amazonNetProfit;
    }
    if (!netMarginPlus) return 0;
    netMarginMinus +=
      totalShippingCosts +
      totalPaymentGatewayCosts +
      totalHandlingFees +
      totalProductCosts +
      totalCustomSpends +
      totalRefunds +
      blendedAdsWithoutCustom +
      totalTaxes;

    var total = 100 - (netMarginMinus * 100) / netMarginPlus;
    return total;
  }
);

const cashTurnover = createSelector(
  [
    totalSales,
    totalShippingCosts,
    totalPaymentGatewayCosts,
    totalCustomSpends,
    totalRefunds,
    blendedAdsWithoutCustom,
    totalTaxes,
  ],
  (
    totalSales,
    totalShippingCosts,
    totalPaymentGatewayCosts,
    totalCustomSpends,
    totalRefunds,
    blendedAdsWithoutCustom,
    totalTaxes
  ) =>
    totalSales -
    totalShippingCosts -
    totalPaymentGatewayCosts -
    totalCustomSpends -
    totalRefunds -
    blendedAdsWithoutCustom -
    totalTaxes
);

const newCustomersCpa = createSelector([blendedAds, new_customers], (blendedAds, new_customers) => {
  if (!new_customers) return 0;
  return blendedAds / new_customers;
});

const newCustomersRoas = createSelector(
  [blendedAds, newCustomerSales],
  (blendedAds, newCustomerSales) => {
    if (!blendedAds) return 0;
    return (newCustomerSales / blendedAds).toFixed(2);
  }
);

const chartNewCustomerRoas = createSelector(
  [chartBlendedAds, chartNewCustomerSales],
  (chartBlendedAds, chartNewCustomerSales) => {
    return simpleChart2(chartNewCustomerSales, chartBlendedAds);
  }
);

const blendedAttributedSales = createSelector(
  [
    facebookPurchaseConversionValue,
    googleConversionValue,
    totalSnapchatConversionPurchasesValue,
    tiktokConversionValue,
    twitterConversionValue,
    bingConversionValue,
    mountainConversionValue,
    criteoConversionValue,
    smsbumpRevenue,
  ],
  (fb, ga, snap, tiktok, mountain, criteo, smsbump) =>
    fb + ga + snap + tiktok + mountain + criteo + smsbump
);

const chartBlendedAttributedSales = createSelector(
  [
    facebookConversionValueChart,
    googleConversionValueChart,
    chartSnapchatConversionPurchasesValue,
    tiktokConversionValueChart,
    twitterConversionValueChart,
    bingConversionValueChart,
    mountainConversionValueChart,
    criteoConversionValueChart,
    smsbumpRevenueChart,
  ],
  (fb, g, s, t, m, sb) => simpleChart3(fb, g, [s, t, m, sb])
);

const blendedAttributedRoas = createSelector(
  [blendedAds, blendedAttributedSales, selectedStatsServices, amazonAdsConversionValue],
  (blendedAds, blendedAttributedSales, selectedStatsServices, amazonAdsConversionValue) => {
    if (selectedStatsServices.includes('amazon')) {
      blendedAttributedSales = (blendedAttributedSales || 0) + (amazonAdsConversionValue || 0);
    }
    if (!blendedAds) return 0;
    return (blendedAttributedSales / blendedAds).toFixed(2);
  }
);

const chartBlendedAttributedRoas = createSelector(
  [chartBlendedAds, chartBlendedAttributedSales, selectedStatsServices, amazonConversionValueChart],
  (
    chartBlendedAds,
    chartBlendedAttributedSales,
    selectedStatsServices,
    amazonConversionValueChart
  ) => {
    let chart1 = chartBlendedAttributedSales;
    if (selectedStatsServices.includes('amazon')) {
      if (amazonConversionValueChart.length && chartBlendedAttributedSales.length) {
        chart1 = simpleChartPlus(chartBlendedAttributedSales, amazonConversionValueChart);
      } else if (amazonConversionValueChart.length) {
        chart1 = amazonConversionValueChart;
      }
    }
    return simpleChart2(chart1, chartBlendedAds);
  }
);

const rcRevenue = createSelector([totalSales, newCustomerSales], (totalSales, newCustomerSales) => {
  return totalSales - newCustomerSales;
});

const chartNewCustomersCpa = createSelector(
  [chartBlendedAds, getShopifyStats],
  (chartBlendedAds, stats) => {
    var chart2 = simpleChart(stats, 'new_customers_count');
    return simpleChart2(chartBlendedAds, chart2);
  }
);

const chartTotalVariantsSold = createSelector([getShopifyStats], (stats) => {
  return simpleChart(stats, 'total_items');
});

const totalVariantsSold = createSelector([chartTotalVariantsSold], (totalVariantsSoldArr) => {
  return (
    totalVariantsSoldArr?.reduce((prev, count) => {
      return prev + (count?.y || 0);
    }, 0) || 0
  );
});

const calcCustomMetric = (metric, data, state, customMetrics, counter, attribution) => {
  let expression: string = metric.expression;
  Object.keys(metric.stats).forEach((statId) => {
    let statResult, _metric;
    let format: valueFormats = 'decimal';
    const stat = metric.stats[statId];
    if (stat.type === 'stat') {
      if (attribution) {
        _metric = Object.values<any>(metrics).find((m) => m.key === stat.value);
        statResult = state[stat.value];
        format = _metric?.format;
      } else {
        _metric = Object.values<any>(SummaryMetrics).find((m) => m.metricId === stat.value);
        statResult = selectors[stat.value](state);
        format = _metric?.type;
      }
    } else if (stat.type === 'nestedStat') {
      // referencing previously saved custom metric. avoid infinite recursion.
      if (stat.value === 'customMetrics') {
        const nestedMetric = customMetrics.find((x) => x.id == stat.statObjectKey);
        format = nestedMetric?.metricType === '%' ? 'percent' : 'decimal';
        statResult =
          data[stat.statObjectKey] ??
          (nestedMetric && counter < 5
            ? iterateMetrics(nestedMetric, data, state, customMetrics, counter)
            : 0);
      } else {
        const fullStat = selectors[stat.value](state);
        statResult = fullStat[stat.statObjectKey] || 0;
      }
      if (typeof statResult === 'object' && stat.specificStat) {
        const _metric = Object.values<any>(SummaryMetrics).find(
          (m) => m.statObjectKey === stat.statObjectKey
        );
        format = _metric?.type;
        statResult = statResult[stat.specificStat] || 0;
      }
    }
    if (format === 'percent') statResult = statResult / 100;
    expression = expression.replace(statId, `(${statResult || 0})`);
  });
  return expression;
};

const calcDeprecatedCustomMetric = (metric, data, state, customMetrics, counter) => {
  let algo = '';
  metric.stat.forEach((stat) => {
    if (stat.type === 'stat') {
      const statResult = selectors[stat.value](state);
      algo += statResult;
    } else if (stat.type === 'operator') {
      // add spaces to deal with negative values eg. (7--4)
      algo += ` ${stat.value} `;
    } else if (stat.type === 'nestedStat') {
      let statResult;
      // referencing previously saved custom metric. avoid infinite recursion.
      if (stat.value === 'customMetrics') {
        const nestedMetric = customMetrics.find((x) => x.id == stat.statObjectKey);
        statResult =
          data[stat.statObjectKey] ??
          (nestedMetric && counter < 5
            ? iterateMetrics(nestedMetric, data, state, customMetrics, counter)
            : 0);
      } else {
        const fullStat = selectors[stat.value](state);
        statResult = fullStat[stat.statObjectKey] || 0;
      }
      if (typeof statResult === 'object' && stat.specificStat) {
        statResult = statResult[stat.specificStat] || 0;
      }
      algo += statResult;
    }
  });
  return algo;
};

const iterateMetrics = (metric, data, state, customMetrics, counter, attribution = false) => {
  try {
    counter++;
    const expression = metric.stats
      ? calcCustomMetric(metric, data, state, customMetrics, counter, attribution)
      : calcDeprecatedCustomMetric(metric, data, state, customMetrics, counter);
    let res = eval(expression);
    res = isFinite(res) ? res : 0;
    if (metric.metricType === '%') {
      res = res * 100;
    }
    return res || 0;
  } catch (e) {
    console.warn(`chart failed for `, metric, e);
  }
};

const customMetrics = createSelector(
  [getCustomMetrics, ...Object.values(partialStateDependencies)],
  ({ metrics }, ...rest) => {
    const state = createPartialState(rest);
    const data = {};
    metrics.forEach((metric) => {
      const result = iterateMetrics(metric, data, state, metrics, 0);
      if (!result) return;
      data[metric.id] = result;
    });

    return data;
  },
  lodashMemoize
);

const customMetricsAttribution = createSelector(
  [getEntireAttributionState, getCustomMetrics],
  (state, { metricsAttribution }) => {
    const data = {};
    metricsAttribution.forEach((metric) => {
      const result = iterateMetrics(metric, data, state, metricsAttribution, 0, true);
      if (!result) return;
      data[metric.id] = result;
    });

    return data;
  }
);

const transformExternalStatsValue = (stats: any[]) => {
  const copy = structuredClone(stats);
  copy.forEach((stat) => {
    Object.entries<any>(stat.hours).forEach(([hour, metric]) => {
      Object.values<any>(metric).forEach((m) => {
        stat.hours[hour][m.metricId] = m.value;
      });
    });
  });
  return copy;
};

const externalApps = createSelector([getExternalApps, getNewStats], (externalApps, stats) => {
  const data = {};
  const clientIds = externalApps?.apps?.map?.((app) => app.id) || emptyArray;
  const externalMetrics = externalApps?.externalMetrics;

  const externalAppsToSum = Object.fromEntries(
    Object.entries(stats).filter(([serviceId]) => clientIds.includes(serviceId))
  );
  Object.entries(externalAppsToSum).forEach(([clientId, app]) => {
    externalMetrics.forEach((metric) => {
      const transformedStats = transformExternalStatsValue(stats[clientId]);
      if (!data[clientId]) {
        data[clientId] = {};
      }
      data[clientId][metric.id] = total(transformedStats, metric.id);
    });
  });

  return data;
});

const externalAppsChart = createSelector([getExternalApps, getNewStats], (externalApps, stats) => {
  const data = {};
  const clientIds = externalApps?.apps?.map?.((app) => app.id) || emptyArray;
  const externalMetrics = externalApps?.externalMetrics;

  const externalAppsToSum = Object.fromEntries(
    Object.entries(stats).filter(([serviceId]) => clientIds.includes(serviceId))
  );
  Object.entries(externalAppsToSum).forEach(([clientId, app]) => {
    externalMetrics.forEach((metric) => {
      const transformedStats = transformExternalStatsValue(stats[clientId]);
      if (!data[clientId]) {
        data[clientId] = {};
      }
      data[clientId][metric.id] = simpleChart(transformedStats, metric.id);
    });
  });

  return data;
});

const getChartFuncByOperator = (operator) => {
  switch (operator) {
    case '+':
      return simpleChart3;
    case '-':
      return simpleChartMinus;
    case '*':
      return simpleChart4;
    case '/':
      return simpleChart2;
    default:
      return null;
  }
};

const getChartFromStat = (stat, data, state, customMetrics, counter) => {
  let chart;
  if (stat.chart === 'customMetricsCharts') {
    const nestedMetric = customMetrics.find((x) => x.id == stat.statObjectKey);
    chart =
      data[stat.statObjectKey] ??
      (nestedMetric && counter < 6
        ? iterateMetricsCharts(nestedMetric, data, state, customMetrics, counter)
        : null);

    if (chart && stat.specificStat) {
      chart = chart[stat.specificStat];
    }
  } else {
    chart = selectors[stat.chart](state);
    if (stat.statObjectKey) {
      chart = chart[stat.statObjectKey];
      if (chart && stat.specificStat) {
        chart = chart[stat.specificStat];
      }
    }
  }
  return chart;
};

const calcCustomMetricChart = (metric, data, state, customMetrics, counter) => {
  const charts = [];
  Object.keys(metric.stats).forEach((statId) => {
    const stat = metric.stats[statId];
    if (!stat || !stat.chart) {
      return;
    }

    const chart = getChartFromStat(stat, data, state, customMetrics, counter);
    if (!chart || chart.length == 0) return;
    charts.push({ id: statId, data: chart });
  });

  const tokens = metric.expression?.match(/field_\d+#/g) || [];
  if (tokens.some((token) => !charts.find((c) => c.id == token))) return;

  // Get all unique x values from all charts
  const xValues = new Set(charts.flatMap((c) => c.data.map((d) => d.x)));

  const result = [];
  // Sum y values for each x value
  for (const x of xValues) {
    let expression = metric.expression;
    charts.forEach((c) => {
      const val = c.data.find((d) => d.x === x);
      expression = expression.replace(c.id, `(${val?.y || 0})`);
    });

    result.push({ x, y: eval(expression) });
  }
  return result;
};
const calcDeprecatedCustomMetricChart = (metric, data, state, customMetrics, counter) => {
  const [stat1, operator, stat2] = metric.stat;
  if (!stat1 || !stat1.chart || !stat2 || !stat2.chart) {
    return [];
  }
  const chart1 = getChartFromStat(stat1, data, state, customMetrics, counter);
  const chart2 = getChartFromStat(stat2, data, state, customMetrics, counter);
  if (!chart1?.length || !chart2?.length) return;
  const chartFunc = getChartFuncByOperator(operator.value);
  const result = chartFunc(chart1, chart2);
  return result;
};

const iterateMetricsCharts = (metric, data, state, customMetrics, counter) => {
  try {
    counter++;
    const result = metric.stats
      ? calcCustomMetricChart(metric, data, state, customMetrics, counter)
      : calcDeprecatedCustomMetricChart(metric, data, state, customMetrics, counter);
    return result;
  } catch (e) {
    console.warn(`chart failed for `, metric, e);
  }
};

const customMetricsCharts = createSelector(
  [getCustomMetrics, ...Object.values(partialStateDependencies)],
  ({ metrics }, ...rest) => {
    const data = {};
    const state = createPartialState(rest);

    metrics.forEach((metric) => {
      const result = iterateMetricsCharts(metric, data, state, metrics, 0);
      if (!result) return;
      data[metric.id] = result;
    });

    return data;
  }
);

const allChartValuesTo100 = (chart) => {
  var max = -1000000000;
  var min = 1000000000;
  var arr = [];
  var len = chart.length;
  for (var i = 0; i < len; i++) {
    // convert negs to posivs
    if (chart[i]['y'] < 0) {
      chart[i]['y'] *= -1;
    }
    if (chart[i]['y'] > max) max = chart[i]['y'];
    if (chart[i]['y'] < min) min = chart[i]['y'];
    arr.push({ ...chart[i] }); // need deep copy, otherwise passed by ref
  }

  for (let i = 0; i < len; i++) {
    if (min == max) {
      arr[i]['y'] = 0;
    } else {
      arr[i]['y'] = (100 * arr[i]['y']) / max;
    }
  }
  return arr;
};

const normalizedCharts = {
  chartRoas: createSelector([chartRoas], allChartValuesTo100),
  chartSales: createSelector([chartSales], allChartValuesTo100),
  chartCogs: createSelector([chartCogs], allChartValuesTo100),
  chartOrders: createSelector([chartOrders], allChartValuesTo100),
  chartGaRevenuePerTransaction: createSelector([chartGaRevenuePerTransaction], allChartValuesTo100),
  chartFacebookInsights: createSelector([chartFacebookInsights], allChartValuesTo100),
  chartGoogleAds: createSelector([chartGoogleAds], allChartValuesTo100),
  chartSnapchatAds: createSelector([chartSnapchatAds], allChartValuesTo100),
  chartGaTransactionsPerSession: createSelector(
    [chartGaTransactionsPerSession],
    allChartValuesTo100
  ),
  chartGaUsers: createSelector([chartGaUsers], allChartValuesTo100),
  chartFacebookCpc: createSelector([chartFacebookCpc], allChartValuesTo100),
  chartFacebookCpm: createSelector([chartFacebookCpm], allChartValuesTo100),
  chartFacebookCtr: createSelector([chartFacebookCtr], allChartValuesTo100),
  chartFacebookPurchaseRoas: createSelector([chartFacebookPurchaseRoas], allChartValuesTo100),
  chartGoogleRoas: createSelector([chartGoogleRoas], allChartValuesTo100),
  chartFacebookCostPerOutboundClick: createSelector(
    [chartFacebookCostPerOutboundClick],
    allChartValuesTo100
  ),
  chartNewCustomersCpa: createSelector([chartNewCustomersCpa], allChartValuesTo100),
  chartKlaviyoPlacedOrderSalesPercentFlows: createSelector(
    [chartKlaviyoPlacedOrderSalesPercentFlows],
    allChartValuesTo100
  ),
  chartKlaviyoPlacedOrderSalesPercentCampaigns: createSelector(
    [chartKlaviyoPlacedOrderSalesPercentCampaigns],
    allChartValuesTo100
  ),
  chartKlaviyoPlacedOrderSalesPercent: createSelector(
    [chartKlaviyoPlacedOrderSalesPercent],
    allChartValuesTo100
  ),
};

//general selectors - couldn't put them in generalSelectorService bc of circular dependency
const generalNetProfit = createSelector(
  [shopifyNetProfit, amazonNetProfit, (state, serviceId) => serviceId],
  (sh, am, serviceId) => {
    if (serviceId === 'amazon') {
      return am;
    } else if (serviceId === 'shopify') {
      return sh;
    } else {
      return 0;
    }
  }
);

const netProfitChart = createSelector(
  [totalNetProfitChart, cahrtAmazonNetProfit, (state, serviceId) => serviceId],
  (chartShopifyNetProfit, chartAmazonNetProfit, serviceId) => {
    if (serviceId === 'amazon') {
      return chartAmazonNetProfit;
    } else if (serviceId === 'shopify') {
      return chartShopifyNetProfit;
    } else {
      return [];
    }
  }
);

const generalNetSales = createSelector(
  [netSales, amazonNetSales, (state, serviceId) => serviceId],
  (shopifyNetSales, amazonNetSales, serviceId) => {
    if (serviceId === 'amazon') {
      return amazonNetSales;
    } else if (serviceId === 'shopify') {
      return shopifyNetSales;
    } else {
      return 0;
    }
  }
);

const generalNetSalesChart = createSelector(
  [netSalesChart, chartAmazonNetSales, (state, serviceId) => serviceId],
  (shopifyNetSalesChart, chartAmazonNetSales, serviceId) => {
    if (serviceId === 'amazon') {
      return chartAmazonNetSales;
    } else if (serviceId === 'shopify') {
      return shopifyNetSalesChart;
    } else {
      return [];
    }
  }
);

const averageOrderValue = createSelector(
  [shopifyAov, amazonAOV, (state, serviceId) => serviceId],
  (shopifyAOV, amazonAOV, serviceId) => {
    if (serviceId === 'amazon') {
      return amazonAOV;
    } else if (serviceId === 'shopify') {
      return shopifyAOV;
    } else {
      return 0;
    }
  }
);

const averageOrderValueChart = createSelector(
  [chartShopifyAov, amazonAOVChart, (state, serviceId) => serviceId],
  (chartShopifyAov, amazonAOVChart, serviceId) => {
    if (serviceId === 'amazon') {
      return amazonAOVChart;
    } else if (serviceId === 'shopify') {
      return chartShopifyAov;
    } else {
      return [];
    }
  }
);

const pixelCostPerAtc = createSelector(
  [blendedAds, tModules.pixelUniqueSessionsAtc],
  (blendedAds, pixelUniqueSessionsAtc) => {
    return safeDivide(blendedAds, pixelUniqueSessionsAtc);
  }
);

const pixelCostPerSession = createSelector(
  [blendedAds, tModules.pixelVisitors],
  (blendedAds, pixelVisitors) => {
    return safeDivide(blendedAds, pixelVisitors);
  }
);

const pixelCostPerAtcChart = createSelector(
  [chartBlendedAds, tModules.pixelUniqueSessionsAtcChart],
  (chartBlendedAds, pixelUniqueSessionsAtcChart) => {
    return simpleChart2(chartBlendedAds, pixelUniqueSessionsAtcChart);
  }
);

const pixelCostPerSessionChart = createSelector(
  [chartBlendedAds, tModules.pixelVisitorsChart],
  (chartBlendedAds, pixelVisitorsChart) => {
    return simpleChart2(chartBlendedAds, pixelVisitorsChart);
  }
);

// MAINLY FOR updateDayStats
const PROPS_TO_SELECTORS = {
  ['ga_sessions']: 'totalGoogleAnalyticsSessions',
  ['ga_transactionRevenue']: 'googleTransactionRevenue',
  ['ga_transactions']: 'googleTransactions',
  ['ga_users']: 'totalGoogleAnalyticsUsers',

  ['gorgias_median_first_response_time_delta']: null,
  ['gorgias_total_new_tickets_value']: 'totalGorgiasTicketsCreated',
  ['gorgias_total_replied_tickets_value']: 'totalGorgiasTicketsReplied',
  ['gorgias_median_first_response_time_value']: 'averageGorgiasResponseTime',
  ['gorgias_median_resolution_time_value']: 'averageGorgiasResolutionTime',
};

const selectors = {
  PROPS_TO_SELECTORS: {
    ...PROPS_TO_SELECTORS,
  },
  ...tServices.SELECTORS,
  ...tModules.SELECTORS,
  chartRoas,
  chartSales,
  chartRefunds,
  totalOrders,
  totalOrdersWithAmount,
  chartOrdersWithAmount,
  totalSales,
  totalRefunds, // for client summary
  totalRoas,
  roas: totalRoas,
  totalPaymentGatewayCosts,
  totalCostsRefunds,
  totalProductCostsOrders,
  totalProductCosts,
  totalHandlingFees,
  totalShippingCosts,
  totalNewGrossSales,
  chartNewGrossSales,
  totalCustomSpends,
  totalNetProfit,
  shopifyNetProfit,
  totalNetProfitChart,
  totalNetMargin,
  cashTurnover,
  chartOrders,
  totalGoogleAnalyticsUsers,
  averageGaRevenuePerTransaction,
  averageGaTransactionsPerSession,
  chartGaTransactionsPerSession,
  chartGaRevenuePerTransaction,
  chartGaUsers,
  totalRevenuePerSession,
  totalGorgiasTicketsCreated,
  totalGorgiasTicketsReplied,
  averageGorgiasResponseTime,
  averageGorgiasResolutionTime,
  totalCustomAdSpendsChart,
  totalCustomNonAdSpendsChart,
  totalCpa,
  totalReturns,
  totalTaxes,
  totalRefundTaxes,
  totalNetTaxes,
  chartTaxes,
  chartNetTaxes,
  chartCogs,
  chartHandlingFees,
  googleTransactions,
  uniqueCustomerLifetimeValue,
  customerFrequency,
  ltvCpa,
  googleTransactionRevenue,
  totalGoogleAnalyticsSessions,
  normalizedCharts,
  chartGoogleAds,
  shopifyAov,
  shopifyAovIncludeZero,
  chartShopifyAov,
  chartShopifyAovIncludeZero,
  shopifyCpa,
  chartShopifyCpa,
  shopifyConversionRate,
  blendedAds,
  totalCustomAdSpends,
  chartGorgiasTicketsCreated,
  chartGorgiasTicketsReplied,
  chartGorgiasResolutionTime,
  chartGorgiasResponseTime,
  new_customers,
  newCustomersPercent,
  oldCustomersPercent,
  chartNewCustomersPercent,
  chartOldCustomersPercent,
  newCustomersOrders,
  chartNewCustomersOrders,
  newCustomersCpa,
  newCustomersRoas,
  chartBlendedAds,
  chartNewCustomersCpa,
  mer,
  merChart,
  chartTotalCpa,
  googleBounceRate,
  totalShippingCostsChart,
  totalPaymentGatewayCostsChart,
  totalVariantsSold,
  chartTotalVariantsSold,
  newCustomerSales,
  blendedAttributedRoas,
  rcRevenue,
  netSales,
  chartNewCustomerSales,
  chartReturningCustomerSales,
  chartNewCustomerRoas,
  googleConversionValueChart,
  chartBlendedAttributedRoas,
  netSalesChart,
  grossProfit,
  grossProfitChart,
  poas,
  poasChart,
  order_tags,
  customMetrics,
  customMetricsAttribution,
  externalApps,
  externalAppsChart,
  customMetricsCharts,
  sources,
  orderTagsCharts,
  sourcesCharts,
  getUniqueCustomerCount,
  allChartValuesTo100,
  totalInventoryItems,
  totalInventoryItemsMissing,
  totalInventoryCost,
  totalInventoryValue,
  getStats,
  getPaymentGatewayCosts,
  getShippingCosts,
  getCustomSpends,
  getMainDatePickerSelectionRange,
  simpleChart,
  simpleChart2,
  simpleChart3,
  simpleChart4,
  simpleChartMinus,
  calculateCustomSpendBySource,
  total_product_cost,
  customSpendBySource,
  totalCustomSpendsByCategory,
  total_handling_fees,
  totalRevenuePerSessionChart,
  totalReturnsChart,
  totalCustomSpendsChart,
  totalCustomNonAdSpends,
  customNonAdSpends,
  generalNetProfit,
  netProfitChart,
  blendedAdsWithoutCustom,
  generalNetSales,
  generalNetSalesChart,
  averageOrderValue,
  averageOrderValueChart,
  totalForRange,
  pixelCostPerAtc,
  pixelCostPerSession,
  pixelCostPerAtcChart,
  pixelCostPerSessionChart,
  totalGrossSales,
  totalShippingPrice,
  // totalShippingTax,
  totalDiscounts,
  totalDiscountsChart,
  totalCustomNonAdSpendsArr,
  totalCustomAdSpendsArr,
  willyMetric,
  willyChart,
  useShippingChargesAsCost,
};

const isBrowser = typeof window === 'object';

if (isBrowser) {
  // @ts-ignore
  window.reselectDebug = () => {
    const res = {};
    Object.keys(selectors).forEach((key) => {
      if (typeof selectors[key] === 'function') {
        try {
          res[key] = selectors[key].recomputations();
        } catch {}
      }
    });
    return res;
  };
}

export default selectors;
